// ═══════════════════════════════════════════════════════════════════════════════
// MXDR365 — Central State Management
// React Context + useReducer for the entire application
// ═══════════════════════════════════════════════════════════════════════════════
import { createContext, useContext, useReducer, useCallback, ReactNode, Dispatch, useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { loadProjectTaskPages, partitionProjectTasks } from '../lib/projectTaskPages';
import type { UnsupportedTaskSummary } from '../lib/projectTaskPages';
import type {
  User, Article, ArticleSummary, Project, Task, TaskStatus, Notification, TrainingProgress, Course,
  TrainingAssignment, TrainingGroup, RemedialRule, Ticket,
  TicketStatus, TimelineEvent, IncidentEscalation, EscalationStatus, RosterSlot,
  CustomRole, UserRoleAssignment, DataRetentionPolicy, ApprovalStatus, KnowledgeBase, Category, ArticleComment,
  FreshServiceConfig, FreshServiceTicket, FreshServiceAnalytics, FreshServiceStats, NotificationPreferences, ThemeMode,
  ThemeSchedule, PresenceUser, ImportJob, Handoff, DecisionTree,
  IncidentResponseCase, IRStatus, IRSeverity, IOC, IRNote, IRTimelineEvent, IRTimelineEventType, IOCType,
  GmTractionState, GmRock, GmScorecardMetric, GmIssue, GmTodo, GmL10Meeting, GmPersonAnalysis,
  GmHiringState, GmRequisition, GmCandidate, GmInterview,
  GmPeopleState, GmReview, GmOneOnOne, GmWellbeingEntry, GmAttritionRecord,
  GmDisciplineState, GmDisciplinaryRecord,
  GmExecReportsState, GmExecReport,
} from '../types';
import { USERS, COURSES, CUSTOMERS, initArticles, initProjects, initProgress, initNotifications, initActivity, initTickets, GLOSSARY, DECISION_TREES, getUserName, CANNED_RESPONSES, TICKET_QUEUES, SAVED_VIEWS, ESCALATION_RULES, initEscalations, initRosterSlots } from '../lib/mockData';
import { isAssignedTo } from '../lib/taskAssignees';
import type { ViewId } from '../types';
import { isViewAllowed, rbacViewKey, isGmView, isKnownUserRole, isKnownView, isKnownNavigationView, isKnownPermissionView, hasLiteralPermission } from '../config/featureVisibility';
import { isPrivilegedRole } from '../config/builtinRoles';

export type AdminTab = 'overview' | 'users' | 'workload' | 'assignments' | 'rbac' | 'kbs' | 'retention' | 'import' | 'settings' | 'loganalytics' | 'auditlog' | 'roster' | 'feature_requests' | 'ioc_maintenance' | 'test_data';

/** The list-valued keys of GmTractionState that TRACTION_UPSERT/REMOVE can target. */
export type TractionListKind = 'rocks' | 'metrics' | 'issues' | 'todos' | 'meetings' | 'people';
/** The list-valued keys of GmHiringState that HIRING_UPSERT/REMOVE can target. */
export type HiringListKind = 'requisitions' | 'candidates' | 'interviews';
/** The list-valued keys of GmPeopleState that PEOPLE_UPSERT/REMOVE can target. */
export type PeopleOpsListKind = 'reviews' | 'oneOnOnes' | 'wellbeing' | 'attrition';
/** The list-valued keys of GmDisciplineState that DISCIPLINE_UPSERT/REMOVE can target. */
export type DisciplineListKind = 'records';
/** The list-valued keys of GmExecReportsState that EXEC_REPORTS_UPSERT/REMOVE can target. */
export type ExecReportsListKind = 'reports';
export type FreshServiceTab = 'dashboard' | 'tickets' | 'metrics' | 'settings';
import { getAutoTheme, getTheme, darkTheme, applyThemeMode } from '../lib/theme';

// ─── Blank user — populated by MSAL via SET_CURRENT_USER once authenticated ───
const BLANK_USER: User = {
  id: '',
  name: 'Authenticating…',
  shift: 'Shift 1',
  tier: 'T1',
  avatar: '👤',
  role: 'analyst',
};

// ─── Toast shape ───
export interface AppToast {
  type: 'error' | 'success' | 'info';
  message: string;
  /** Machine-readable error code, e.g. "KB_IMPORT_NO_FILES" */
  code?: string;
}

// ─── Default retention policies ───
const DEFAULT_RETENTION_POLICIES: DataRetentionPolicy[] = [
  { id: 'ret-articles', entityType: 'articles', label: 'Articles', retentionDays: 1825, autoArchive: true, autoDelete: false, archiveAfterDays: 365, deleteAfterDays: null, storageUsedMB: 12.4, recordCount: 0, lastRunAt: null, enabled: true },
  { id: 'ret-handoffs', entityType: 'handoffs', label: 'Shift Handoffs', retentionDays: 365, autoArchive: true, autoDelete: true, archiveAfterDays: 90, deleteAfterDays: 365, storageUsedMB: 2.1, recordCount: 0, lastRunAt: null, enabled: true },
  { id: 'ret-tickets', entityType: 'tickets', label: 'Tickets', retentionDays: 2555, autoArchive: true, autoDelete: false, archiveAfterDays: 730, deleteAfterDays: null, storageUsedMB: 45.8, recordCount: 0, lastRunAt: null, enabled: true },
  { id: 'ret-training', entityType: 'training', label: 'Training Records', retentionDays: 2555, autoArchive: false, autoDelete: false, archiveAfterDays: 730, deleteAfterDays: null, storageUsedMB: 8.3, recordCount: 0, lastRunAt: null, enabled: true },
  { id: 'ret-projects', entityType: 'projects', label: 'Projects & Tasks', retentionDays: 730, autoArchive: true, autoDelete: false, archiveAfterDays: 365, deleteAfterDays: null, storageUsedMB: 5.2, recordCount: 0, lastRunAt: null, enabled: true },
  { id: 'ret-notifications', entityType: 'notifications', label: 'Notifications', retentionDays: 90, autoArchive: false, autoDelete: true, archiveAfterDays: 30, deleteAfterDays: 90, storageUsedMB: 0.8, recordCount: 0, lastRunAt: null, enabled: true },
  { id: 'ret-activity', entityType: 'activity', label: 'Activity Logs', retentionDays: 180, autoArchive: false, autoDelete: true, archiveAfterDays: 90, deleteAfterDays: 180, storageUsedMB: 3.1, recordCount: 0, lastRunAt: null, enabled: true },
];

const DEFAULT_NOTIFICATION_PREFS: NotificationPreferences = {
  userId: '',
  channels: { inApp: true, email: true, teams: false },
  categories: {
    ticket_created: true, ticket_assigned: true, ticket_resolved: true, ticket_escalated: true,
    article_verified: true, article_stale: true, handoff_posted: true,
    task_assigned: true, task_overdue: true, training_due: true,
    mention: true, system: true,
  },
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '08:00',
  updatedAt: new Date().toISOString(),
};

// ─── State shape ───
export interface AppState {
  // Auth
  user: User;
  impersonatingFrom: string | null; // original admin userId when impersonating
  originalUser: User | null;        // full original user object (real MSAL user may not be in USERS[])
  // Navigation
  view: ViewId;
  sidebarOpen: boolean;
  searchOpen: boolean;
  searchQuery: string;
  // Data
  categories: Category[];
  articles: Article[];
  projects: Project[];
  /** Tasks that aren't tied to any project. Nested project tasks still live
   *  under `projects[].tasks`; this bucket holds rows with `projectId: null`
   *  created from the global Tasks view ("standalone" tasks). */
  standaloneTasks: Task[];
  unsupportedProjectTasks: UnsupportedTaskSummary[];
  handoffs: Handoff[];
  tickets: Ticket[];
  progress: Record<string, TrainingProgress[]>;
  /** SCORM course catalog (from Cosmos `courses` via /api/training/courses).
   *  Loaded lazily by useEnsureTrainingProgress alongside progress. */
  courses: Course[];
  /** Course assignments (audience: all / users / group). Loaded with courses. */
  trainingAssignments: TrainingAssignment[];
  /** Named member cohorts for targeted / remedial training. Loaded with courses. */
  trainingGroups: TrainingGroup[];
  /** QA-score remedial auto-assignment rule. Loaded with courses. */
  remedialRule: RemedialRule | null;
  notifications: Notification[];
  // Sub-navigation state
  selectedArticleId: string | null;
  selectedCategoryId: string | null;
  selectedProjectId: string | null;
  selectedTaskId: string | null;
  selectedTicketId: string | null;
  // Page-level detail selections (persisted via URL hash)
  selectedCustomerClient: string | null;
  expandedFsTicketId: number | null;
  selectedDecisionTree: string | null;
  expandedProjectTask: string | null;
  expandedAuditEntry: string | null;
  pmMode: 'table' | 'board' | 'timeline';
  lmsTab: 'courses' | 'compliance' | 'tracking' | 'upload' | 'assignments' | 'groups';
  adminTab: AdminTab;
  freshServiceTab: FreshServiceTab;
  editorArticleId: string | null;
  editorCategoryId: string | null;
  // SOC Manager features
  escalations: IncidentEscalation[];
  rosterSlots: RosterSlot[];
  // Incident Response cases
  incidentResponses: IncidentResponseCase[];
  selectedIRCaseId: string | null;
  // Provisioning — deep-linked request id (email/Teams link → auto-expand the row)
  selectedProvisioningRequestId: string | null;
  // Toasts
  toast: string | AppToast | null;
  // RBAC
  customRoles: CustomRole[];
  roleAssignments: UserRoleAssignment[];
  // Data Retention
  retentionPolicies: DataRetentionPolicy[];
  // FreshService
  freshServiceConfig: FreshServiceConfig;
  freshServiceStats: FreshServiceStats | null;
  freshServiceTickets: FreshServiceTicket[];
  freshServiceAnalytics: FreshServiceAnalytics | null;
  // Notification preferences
  notificationPrefs: NotificationPreferences;
  // Theme
  themeSchedule: ThemeSchedule;
  activeThemeMode: 'dark' | 'light';
  // Presence / collaboration
  presence: PresenceUser[];
  /** Server-side online users (from /api/presence/online).  Populated by a
   *  background heartbeat — accurate across devices/browsers, unlike
   *  `presence` which only reflects this browser's BroadcastChannel. */
  onlineUsers: PresenceUser[];
  // Import jobs
  importJobs: ImportJob[];
  // Knowledge Bases
  knowledgeBases: KnowledgeBase[];
  activeKnowledgeBaseId: string;
  // Decision Trees (from Cosmos DB — merged with built-in trees)
  dbDecisionTrees: DecisionTree[];
  // Registered users (from Cosmos DB)
  registeredUsers: User[];
  // Enhanced project management
  sprints: import('../types').Sprint[];
  goals: import('../types').ProjectGoal[];
  projectTemplates: import('../types').ProjectTemplate[];
  savedProjectViews: import('../types').SavedProjectView[];
  /** Admin override for the Kanban column list. `null` = no override stored,
   *  fall back to the defaults in src/config/projectColumns.ts. Loaded lazily
   *  and never blocks the board from rendering. */
  projectColumnsOverride: string[] | null;
  /** Active sprint filter for board/list views */
  activeSprintId: string | null;
  /** Currently selected saved view (per session) */
  activeSavedViewId: string | null;
  /** Calendar → Timeline → Project drill-in chain.  When set, the Calendar
   *  view replaces its grid with a Timeline scoped to this project; a
   *  drill-through to project detail keeps this set so the project page can
   *  show "Back to Timeline" / "Back to Calendar" breadcrumbs. Cleared by
   *  the "Back to Calendar" affordance. */
  calendarDrillProjectId: string | null;
  /** GM — EOS/Traction data (rocks, scorecard, issues, L10, People Analyzer).
   *  Loaded lazily by useEnsureTraction on first mount of TractionPage. */
  traction: GmTractionState;
  /** GM — Hiring data (requisitions, candidates, interviews).
   *  Loaded lazily by useEnsureHiring on first mount of HiringPage. */
  hiring: GmHiringState;
  /** GM — People-Ops data (reviews, 1:1s, wellbeing, attrition). Named
   *  peopleOps to avoid clashing with traction.people (People Analyzer).
   *  Loaded lazily by useEnsurePeople on first mount of PeoplePage. */
  peopleOps: GmPeopleState;
  /** GM — Discipline data (disciplinary records / 3-strike standing).
   *  Loaded lazily by useEnsureDiscipline on first mount of DisciplinePage. */
  discipline: GmDisciplineState;
  /** GM — Executive-report data (AI-generated period summaries). Loaded
   *  lazily by useEnsureExecReports on first mount of ExecReportsPage. */
  execReports: GmExecReportsState;
}

// ─── Actions ───
export type AppAction =
  | { type: 'SET_USER'; userId: string }
  | { type: 'SET_CURRENT_USER'; user: User }  // Set from real Entra ID (MSAL)
  | { type: 'SET_USER_SHIFT'; shift: 'Shift 1' | 'Shift 2' | 'Shift 3' } // Persisted to /api/me
  | { type: 'SWITCH_ROLE'; role: User['role'] }  // Admin toggle — view the app as a different role
  | { type: 'IMPERSONATE_USER'; userId: string }
  | { type: 'STOP_IMPERSONATION' }
  | { type: 'SET_VIEW'; view: ViewId; entityId?: string }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_SIDEBAR'; open: boolean }
  | { type: 'SET_SEARCH_OPEN'; open: boolean }
  | { type: 'SET_SEARCH_QUERY'; query: string }
  | { type: 'UPDATE_ARTICLE'; article: Article }
  | { type: 'MOVE_ARTICLES'; articleIds: string[]; targetCategoryId: string }
  | { type: 'CREATE_ARTICLE'; article: Article }
  | { type: 'VOTE_ARTICLE'; articleId: string; vote: 'up' | 'down' }
  | { type: 'VERIFY_ARTICLE'; articleId: string }
  | { type: 'BULK_VERIFY_ARTICLES'; articleIds: string[] }
  | { type: 'SET_PM_MODE'; mode: 'table' | 'board' | 'timeline' }
  | { type: 'CALENDAR_DRILL_INTO_PROJECT'; projectId: string }
  | { type: 'CALENDAR_DRILL_CLEAR' }
  | { type: 'SET_LMS_TAB'; tab: 'courses' | 'compliance' | 'tracking' | 'upload' | 'assignments' | 'groups' }
  | { type: 'SET_ADMIN_TAB'; tab: AdminTab }
  | { type: 'SET_TRACTION_DATA'; data: Partial<GmTractionState> }
  // Per-item traction merges — computed against CURRENT state inside the
  // reducer so overlapping in-flight mutations can't revert each other
  // (a whole-array patch built in a stale closure would).
  | { type: 'TRACTION_UPSERT'; kind: TractionListKind; item: GmRock | GmScorecardMetric | GmIssue | GmTodo | GmL10Meeting | GmPersonAnalysis; insertAt?: 'start' | 'end' }
  | { type: 'TRACTION_REMOVE'; kind: TractionListKind; ids: string[] }
  | { type: 'SET_HIRING_DATA'; data: Partial<GmHiringState> }
  // Per-item hiring merges — same current-state rationale as TRACTION_UPSERT.
  | { type: 'HIRING_UPSERT'; kind: HiringListKind; item: GmRequisition | GmCandidate | GmInterview; insertAt?: 'start' | 'end' }
  | { type: 'HIRING_REMOVE'; kind: HiringListKind; ids: string[] }
  | { type: 'SET_PEOPLE_DATA'; data: Partial<GmPeopleState> }
  // Per-item people-ops merges — same current-state rationale as TRACTION_UPSERT.
  | { type: 'PEOPLE_UPSERT'; kind: PeopleOpsListKind; item: GmReview | GmOneOnOne | GmWellbeingEntry | GmAttritionRecord; insertAt?: 'start' | 'end' }
  | { type: 'PEOPLE_REMOVE'; kind: PeopleOpsListKind; ids: string[] }
  | { type: 'SET_DISCIPLINE_DATA'; data: Partial<GmDisciplineState> }
  // Per-item discipline merges — same current-state rationale as TRACTION_UPSERT.
  | { type: 'DISCIPLINE_UPSERT'; kind: DisciplineListKind; item: GmDisciplinaryRecord; insertAt?: 'start' | 'end' }
  | { type: 'DISCIPLINE_REMOVE'; kind: DisciplineListKind; ids: string[] }
  | { type: 'SET_EXEC_REPORTS_DATA'; data: Partial<GmExecReportsState> }
  // Per-item exec-report merges — same current-state rationale as TRACTION_UPSERT.
  | { type: 'EXEC_REPORTS_UPSERT'; kind: ExecReportsListKind; item: GmExecReport; insertAt?: 'start' | 'end' }
  | { type: 'EXEC_REPORTS_REMOVE'; kind: ExecReportsListKind; ids: string[] }
  | { type: 'SET_FS_TAB'; tab: FreshServiceTab }
  | { type: 'CYCLE_TASK_STATUS'; taskId: string }
  | { type: 'UPDATE_TASK'; task: Partial<Task> & { id: string; projectId: string | null } }
  | { type: 'ADD_TASK_COMMENT'; taskId: string; projectId: string | null; comment: string }
  // Enhanced PM actions
  | { type: 'LOG_TASK_TIME'; taskId: string; projectId: string | null; minutes: number; billable?: boolean; note?: string; workDate?: string }
  | { type: 'DELETE_TASK_TIME'; taskId: string; projectId: string | null; entryId: string }
  | { type: 'ADD_TASK_CHECKLIST'; taskId: string; projectId: string | null; text: string }
  | { type: 'TOGGLE_TASK_CHECKLIST'; taskId: string; projectId: string | null; itemId: string }
  | { type: 'REMOVE_TASK_CHECKLIST'; taskId: string; projectId: string | null; itemId: string }
  | { type: 'SET_TASK_DEPENDENCIES'; taskId: string; projectId: string | null; blockedByIds: string[] }
  | { type: 'TOGGLE_TASK_WATCHER'; taskId: string; projectId: string | null; userId: string }
  | { type: 'BULK_UPDATE_TASKS'; taskIds: string[]; updates: Partial<Task> }
  | { type: 'BULK_ARCHIVE_TASKS'; taskIds: string[] }
  | { type: 'ADD_PROJECT_STATUS_UPDATE'; projectId: string; update: { authorId: string; health: 'on-track' | 'at-risk' | 'off-track' | 'on-hold'; summary: string; detail: string } }
  | { type: 'DELETE_PROJECT_STATUS_UPDATE'; projectId: string; updateId: string }
  | { type: 'CREATE_SUBTASK'; parentTaskId: string; projectId: string | null; task: Task }
  | { type: 'CREATE_SPRINT'; projectId: string; sprint: import('../types').Sprint }
  | { type: 'UPDATE_SPRINT'; sprintId: string; updates: Partial<import('../types').Sprint> }
  | { type: 'DELETE_SPRINT'; sprintId: string }
  | { type: 'SET_SPRINTS'; sprints: import('../types').Sprint[] }
  | { type: 'CREATE_GOAL'; goal: import('../types').ProjectGoal }
  | { type: 'UPDATE_GOAL'; goalId: string; updates: Partial<import('../types').ProjectGoal> }
  | { type: 'DELETE_GOAL'; goalId: string }
  | { type: 'SET_GOALS'; goals: import('../types').ProjectGoal[] }
  | { type: 'CREATE_TEMPLATE'; template: import('../types').ProjectTemplate }
  | { type: 'DELETE_TEMPLATE'; templateId: string }
  | { type: 'SET_TEMPLATES'; templates: import('../types').ProjectTemplate[] }
  | { type: 'CREATE_PROJECT_VIEW'; view: import('../types').SavedProjectView }
  | { type: 'UPDATE_PROJECT_VIEW'; viewId: string; updates: Partial<import('../types').SavedProjectView> }
  | { type: 'DELETE_PROJECT_VIEW'; viewId: string }
  | { type: 'SET_PROJECT_VIEWS'; views: import('../types').SavedProjectView[] }
  | { type: 'ARCHIVE_PROJECT'; projectId: string }
  | { type: 'RESTORE_PROJECT'; projectId: string }
  | { type: 'COMPLETE_MODULE'; userId: string; courseId: string; moduleId: string; score: number | null }
  | { type: 'CREATE_TICKET'; ticket: Ticket }
  | { type: 'UPDATE_TICKET'; ticket: Partial<Ticket> & { id: string } }
  | { type: 'SET_TICKET_STATUS'; ticketId: string; status: TicketStatus }
  | { type: 'ASSIGN_TICKET'; ticketId: string; assigneeId: string }
  | { type: 'ADD_TICKET_COMMENT'; ticketId: string; content: string; isInternal: boolean }
  | { type: 'SELECT_TICKET'; id: string | null }
  | { type: 'ESCALATE_TICKET'; ticketId: string }
  | { type: 'MERGE_TICKET'; sourceId: string; targetId: string }
  | { type: 'ADD_WATCHER'; ticketId: string; userId: string }
  | { type: 'REMOVE_WATCHER'; ticketId: string; userId: string }
  | { type: 'LOG_TIME'; ticketId: string; minutes: number; billable: boolean; note: string }
  | { type: 'APPROVE_TICKET'; ticketId: string; approverId: string; status: 'approved' | 'rejected'; comment: string }
  | { type: 'ADD_TIMELINE_EVENT'; ticketId: string; event: TimelineEvent }
  | { type: 'RATE_TICKET'; ticketId: string; score: number; comment: string }
  | { type: 'READ_NOTIFICATION'; notifId: string }
  | { type: 'READ_ALL_NOTIFICATIONS' }
  | { type: 'TOAST'; message: string | AppToast | null }
  | { type: 'SELECT_ARTICLE'; id: string | null }
  | { type: 'SELECT_PROJECT'; id: string | null }
  | { type: 'SELECT_TASK'; id: string | null }
  | { type: 'SELECT_CUSTOMER_CLIENT'; name: string | null }
  | { type: 'SELECT_FS_TICKET'; id: number | null }
  | { type: 'SELECT_DECISION_TREE'; id: string | null }
  | { type: 'SELECT_PROJECT_TASK'; id: string | null }
  | { type: 'SELECT_AUDIT_ENTRY'; id: string | null }
  | { type: 'OPEN_EDITOR'; articleId: string | null; categoryId?: string }
  | { type: 'DELETE_ARTICLE'; articleId: string }
  | { type: 'CREATE_CATEGORY'; category: Category }
  | { type: 'DELETE_CATEGORY'; categoryId: string }
  | { type: 'ADD_ARTICLE_COMMENT'; articleId: string; content: string }
  | { type: 'DELETE_ARTICLE_COMMENT'; articleId: string; commentId: string }
  // Escalation workflow
  | { type: 'CREATE_ESCALATION'; escalation: IncidentEscalation }
  | { type: 'ACKNOWLEDGE_ESCALATION'; id: string; userId: string }
  | { type: 'RESOLVE_ESCALATION'; id: string; userId: string }
  | { type: 'BUMP_SEVERITY'; id: string }
  | { type: 'ASSIGN_ESCALATION'; id: string; userId: string }
  // Incident Response
  | { type: 'SET_IR_CASES'; cases: IncidentResponseCase[] }
  | { type: 'CREATE_IR_CASE'; irCase: IncidentResponseCase }
  | { type: 'UPDATE_IR_CASE'; irCase: Partial<IncidentResponseCase> & { id: string } }
  | { type: 'REPLACE_IR_CASE'; irCase: IncidentResponseCase }
  | { type: 'DELETE_IR_CASE'; id: string }
  | { type: 'CLOSE_IR_CASE'; id: string; userId: string }
  | { type: 'ADD_IR_NOTE'; caseId: string; note: IRNote }
  | { type: 'ADD_IR_IOC'; caseId: string; ioc: IOC }
  | { type: 'REMOVE_IR_IOC'; caseId: string; iocId: string }
  | { type: 'ADD_IR_TIMELINE_EVENT'; caseId: string; event: IRTimelineEvent }
  | { type: 'SELECT_IR_CASE'; id: string | null }
  // Roster management
  | { type: 'UPDATE_ROSTER_SLOT'; slotId: string; userId: string | null }
  | { type: 'TOGGLE_SLOT_INACTIVE'; slotId: string }
  // RBAC
  | { type: 'CREATE_CUSTOM_ROLE'; role: CustomRole }
  | { type: 'UPDATE_CUSTOM_ROLE'; role: CustomRole }
  | { type: 'DELETE_CUSTOM_ROLE'; roleId: string }
  | { type: 'ASSIGN_ROLE'; assignment: UserRoleAssignment }
  | { type: 'UNASSIGN_ROLE'; userId: string; roleId: string }
  // Data Retention
  | { type: 'UPDATE_RETENTION_POLICY'; policy: DataRetentionPolicy }
  // FreshService
  | { type: 'SET_FRESHSERVICE_CONFIG'; config: FreshServiceConfig }
  | { type: 'SET_FRESHSERVICE_STATS'; stats: FreshServiceStats }
  | { type: 'SET_FS_TICKETS'; tickets: FreshServiceTicket[] }
  | { type: 'SET_FS_ANALYTICS'; analytics: FreshServiceAnalytics }
  | { type: 'ADD_FS_TICKET'; ticket: FreshServiceTicket }
  | { type: 'UPDATE_FS_TICKET'; ticket: FreshServiceTicket }
  // Notification Preferences
  | { type: 'UPDATE_NOTIFICATION_PREFS'; prefs: Partial<NotificationPreferences> }
  // Theme
  | { type: 'SET_THEME_MODE'; mode: ThemeMode }
  | { type: 'SET_ACTIVE_THEME'; mode: 'dark' | 'light' }
  | { type: 'UPDATE_THEME_SCHEDULE'; schedule: Partial<ThemeSchedule> }
  // Presence
  | { type: 'SET_PRESENCE'; users: PresenceUser[] }
  | { type: 'UPDATE_PRESENCE'; user: PresenceUser }
  | { type: 'PRUNE_PRESENCE'; cutoff: number }
  | { type: 'SET_ONLINE_USERS'; users: PresenceUser[] }
  // Import
  | { type: 'ADD_IMPORT_JOB'; job: ImportJob }
  | { type: 'UPDATE_IMPORT_JOB'; job: Partial<ImportJob> & { id: string } }
  // Knowledge Bases
  | { type: 'SET_KNOWLEDGE_BASES'; kbs: KnowledgeBase[] }
  | { type: 'SET_ACTIVE_KB'; kbId: string }
  // Categories
  | { type: 'SET_CATEGORIES'; categories: Category[] }
  | { type: 'UPDATE_CATEGORY'; category: Partial<Category> & { id: string } }
  // Bulk API loads
  | { type: 'SET_ARTICLES'; articles: Article[] }
  | { type: 'SET_ARTICLE_SUMMARIES'; articles: ArticleSummary[] }
  | { type: 'SET_FULL_ARTICLE'; article: Article }
  | { type: 'SET_PROJECTS'; projects: Project[] }
  | { type: 'SET_PROJECT_DETAIL'; project: Project; unsupportedTasks?: UnsupportedTaskSummary[] }
  | { type: 'SET_UNSUPPORTED_PROJECT_TASKS'; tasks: UnsupportedTaskSummary[] }
  | { type: 'SET_STANDALONE_TASKS'; tasks: Task[] }
  | { type: 'SET_HANDOFFS'; handoffs: Handoff[] }
  | { type: 'ADD_HANDOFF'; handoff: Handoff }
  | { type: 'UPDATE_HANDOFF'; handoff: Handoff }
  | { type: 'ARCHIVE_HANDOFF'; handoffId: string }
  | { type: 'SET_TICKETS'; tickets: Ticket[] }
  | { type: 'SET_ESCALATIONS'; escalations: IncidentEscalation[] }
  | { type: 'SET_ROSTER_SLOTS'; rosterSlots: RosterSlot[] }
  | { type: 'SET_NOTIFICATIONS'; notifications: Notification[] }
  // Bulk loads — RBAC, training
  | { type: 'SET_CUSTOM_ROLES'; roles: CustomRole[] }
  | { type: 'SET_ROLE_ASSIGNMENTS'; assignments: UserRoleAssignment[] }
  | { type: 'SET_TRAINING_PROGRESS'; progress: Record<string, TrainingProgress[]> }
  | { type: 'SET_COURSES'; courses: Course[] }
  // Local cache update after api.training.uploadScorm succeeds — the page
  // calls the API directly (multipart), so this action is UI-only.
  | { type: 'ADD_COURSE'; course: Course }
  // Local cache update after api.training.updateCourse succeeds (UI-only).
  | { type: 'UPDATE_COURSE'; course: Course }
  // Training assignments / groups / remedial rule — pages call the API
  // directly and dispatch these as local cache updates (all UI-only).
  | { type: 'SET_TRAINING_ASSIGNMENTS'; assignments: TrainingAssignment[] }
  | { type: 'UPSERT_TRAINING_ASSIGNMENT'; assignment: TrainingAssignment }
  | { type: 'DELETE_TRAINING_ASSIGNMENT'; assignmentId: string }
  | { type: 'SET_TRAINING_GROUPS'; groups: TrainingGroup[] }
  | { type: 'UPSERT_TRAINING_GROUP'; group: TrainingGroup }
  | { type: 'DELETE_TRAINING_GROUP'; groupId: string }
  | { type: 'SET_REMEDIAL_RULE'; rule: RemedialRule }
  // Replace one user's progress record with the server's copy (returned by
  // scorm-complete / scorm-bookmark) — used by the SCORM player.
  | { type: 'UPSERT_PROGRESS'; progress: TrainingProgress }
  // Project mutations
  | { type: 'CREATE_PROJECT'; project: Project }
  | { type: 'UPDATE_PROJECT'; project: Partial<Project> & { id: string } }
  | { type: 'CREATE_TASK'; projectId: string | null; task: Task }
  | { type: 'DELETE_TASK'; projectId: string | null; taskId: string }
  | { type: 'DELETE_PROJECT'; projectId: string }
  // Kanban columns on the All Projects board — admin override list only.
  // Defaults are hardcoded in src/config/projectColumns.ts; the board renders
  // synchronously so there is no "loading columns" failure class to handle.
  | { type: 'SET_PROJECT_COLUMNS_OVERRIDE'; columns: string[] | null }
  | { type: 'MOVE_PROJECT_TO_COLUMN'; projectId: string; column: string }
  // Decision Trees
  | { type: 'SET_DECISION_TREES'; trees: DecisionTree[] }
  | { type: 'ADD_DECISION_TREE'; tree: DecisionTree }
  | { type: 'UPDATE_DECISION_TREE'; tree: DecisionTree }
  | { type: 'DELETE_DECISION_TREE'; treeId: string }
  // Registered users
  | { type: 'SET_REGISTERED_USERS'; users: User[] }
  | { type: 'UPDATE_REGISTERED_USER'; userId: string; updates: { tier?: User['tier']; shift?: User['shift']; role?: User['role'] } }
  | { type: 'REMOVE_REGISTERED_USER'; userId: string };

// ─── Initial state ───
const VALID_VIEWS: ViewId[] = [
  'home', 'kql', 'verify', 'article', 'editor', 'kb',
  'mitre', 'decisions', 'ai', 'handoff',
  'projects', 'tasks', 'mytasks',
  'calendar', 'workload',
  'tickets', 'ticket',
  'customers', 'glossary', 'lms',
  'admin', 'users',
  'onboarding',
  'sla', 'escalations', 'roster', 'checklist', 'qa', 'qa_admin', 'coverage',
  'ir', 'threatHunting', 'engineering', 'provisioning',
  'reports', 'freshservice', 'customerPortal',
  'signin-log', 'api_keys',
  'my-feature-requests',
  'my-engineering-requests',
  'gm', 'traction', 'hiring', 'people', 'discipline', 'exec-reports',
];

function getPersistedView(): ViewId {
  try {
    const saved = localStorage.getItem('mxdr365_view');
    if (saved && VALID_VIEWS.includes(saved as ViewId)) {
      return saved as ViewId;
    }
  } catch { /* ignore */ }
  return 'home';
}

function persistView(view: ViewId) {
  try {
    localStorage.setItem('mxdr365_view', view);
  } catch { /* ignore */ }
}

function getPersistedCategories(): Category[] {
  try {
    const saved = localStorage.getItem('mxdr365_categories');
    if (saved) return JSON.parse(saved) as Category[];
  } catch { /* ignore */ }
  return [];
}

function persistCategories(categories: Category[]) {
  try {
    localStorage.setItem('mxdr365_categories', JSON.stringify(categories));
  } catch { /* ignore */ }
}

// ─── URL hash state (persists through page refresh) ───
interface HashState {
  view?: ViewId;
  adminTab?: AdminTab;
  freshServiceTab?: FreshServiceTab;
  lmsTab?: 'courses' | 'compliance' | 'tracking' | 'upload' | 'assignments' | 'groups';
  pmMode?: 'table' | 'board' | 'timeline';
  selectedArticleId?: string;
  selectedProjectId?: string;
  selectedTaskId?: string;
  selectedTicketId?: string;
  selectedCustomerClient?: string;
  expandedFsTicketId?: number;
  selectedDecisionTree?: string;
  expandedProjectTask?: string;
  expandedAuditEntry?: string;
  // KB-specific: selected category (folder) and active knowledge base
  selectedCategoryId?: string;
  activeKnowledgeBaseId?: string;
  // Incident Response — drill-in
  selectedIRCaseId?: string;
  // Provisioning — deep-linked request id
  selectedProvisioningRequestId?: string;
  // Calendar → Timeline → Project drill-in chain
  calendarDrillProjectId?: string;
}

/** Views whose URL hash can carry a `t=<taskId>` param so the Task Drawer
 *  opens deep-linked. Any task-list-like surface that drills into the drawer
 *  should be in here. */
const TASK_DRAWER_VIEWS: ViewId[] = [
  'projects', 'tasks', 'mytasks', 'calendar', 'workload',
];

const ADMIN_TABS: AdminTab[] = ['overview', 'users', 'workload', 'assignments', 'rbac', 'kbs', 'retention', 'import', 'settings', 'loganalytics', 'auditlog', 'roster', 'feature_requests', 'ioc_maintenance', 'test_data'];
const FS_TABS: FreshServiceTab[] = ['dashboard', 'tickets', 'metrics', 'settings'];
const LMS_TABS = ['courses', 'compliance', 'tracking', 'upload', 'assignments', 'groups'] as const;
const PM_MODES = ['table', 'board', 'timeline'] as const;

function readHashState(): HashState {
  try {
    const hash = window.location.hash.slice(1);
    if (!hash) return {};
    const p = new URLSearchParams(hash);
    const result: HashState = {};

    const v = p.get('v');
    // Legacy: 'mytasks' was renamed to the global 'tasks' view. Old bookmarks
    // (#v=mytasks&id=…) silently resolve to the new id.
    const vNormalized = v === 'mytasks' ? 'tasks' : v;
    if (vNormalized && VALID_VIEWS.includes(vNormalized as ViewId)) result.view = vNormalized as ViewId;

    const tab = p.get('tab');
    const view = result.view ?? getPersistedView();
    if (tab) {
      if ((view === 'admin' || view === 'users') && ADMIN_TABS.includes(tab as AdminTab)) {
        result.adminTab = tab as AdminTab;
      } else if ((view === 'freshservice' || view === 'tickets' || view === 'ticket') && FS_TABS.includes(tab as FreshServiceTab)) {
        result.freshServiceTab = tab as FreshServiceTab;
      } else if (view === 'lms' && LMS_TABS.includes(tab as typeof LMS_TABS[number])) {
        result.lmsTab = tab as typeof LMS_TABS[number];
      }
    }

    const mode = p.get('mode');
    if (mode && PM_MODES.includes(mode as typeof PM_MODES[number])) result.pmMode = mode as typeof PM_MODES[number];

    const id = p.get('id');
    if (id) {
      if (view === 'article' || view === 'editor') result.selectedArticleId = id;
      else if (view === 'projects') result.selectedProjectId = id;
      else if (view === 'ticket' || view === 'tickets') result.selectedTicketId = id;
      else if (view === 'tasks' || view === 'mytasks') result.selectedTaskId = id;
      else if (view === 'ir') result.selectedIRCaseId = id;
      else if (view === 'provisioning') result.selectedProvisioningRequestId = id;
    }

    // Page-level detail selections
    const client = p.get('client');
    if (client && view === 'customers') result.selectedCustomerClient = decodeURIComponent(client);

    const fst = p.get('fst');
    if (fst && view === 'freshservice') result.expandedFsTicketId = parseInt(fst, 10) || undefined;

    const dt = p.get('dt');
    if (dt && view === 'decisions') result.selectedDecisionTree = dt;

    // `t` is the canonical task drawer param (Phase 1). Accept legacy `pt`
    // for one release so old bookmarks keep opening the drawer.
    const t = p.get('t') || p.get('pt');
    if (t && TASK_DRAWER_VIEWS.includes(view as ViewId)) result.expandedProjectTask = t;

    const ae = p.get('ae');
    if (ae && (view === 'admin' || view === 'users')) result.expandedAuditEntry = ae;

    // KB + category (folder) — apply to article and kb views so share links work
    const kb = p.get('kb');
    if (kb && (view === 'kb' || view === 'article' || view === 'editor')) result.activeKnowledgeBaseId = kb;

    const cat = p.get('cat');
    if (cat && view === 'kb') result.selectedCategoryId = cat;

    // Calendar drill-in — present on `calendar` (Timeline scope) and `projects`
    // (project detail with calendar/timeline breadcrumbs).
    const cdpid = p.get('cdpid');
    if (cdpid && (view === 'calendar' || view === 'projects')) {
      result.calendarDrillProjectId = cdpid;
    }

    return result;
  } catch { return {}; }
}

function buildHash(
  view: ViewId,
  adminTab: AdminTab,
  freshServiceTab: FreshServiceTab,
  lmsTab: string,
  pmMode: string,
  selectedArticleId: string | null,
  selectedProjectId: string | null,
  selectedTaskId: string | null,
  selectedTicketId: string | null,
  selectedCustomerClient: string | null,
  expandedFsTicketId: number | null,
  selectedDecisionTree: string | null,
  expandedProjectTask: string | null,
  expandedAuditEntry: string | null,
  selectedCategoryId: string | null,
  activeKnowledgeBaseId: string | null,
  selectedIRCaseId: string | null,
  calendarDrillProjectId: string | null,
  selectedProvisioningRequestId: string | null,
): string {
  const p = new URLSearchParams();
  p.set('v', view);

  if (view === 'admin' || view === 'users') p.set('tab', adminTab);
  else if (view === 'freshservice' || view === 'tickets' || view === 'ticket') p.set('tab', freshServiceTab);
  else if (view === 'lms') p.set('tab', lmsTab);

  if (view === 'projects') p.set('mode', pmMode);

  const id = view === 'ir'
    ? selectedIRCaseId
    : view === 'provisioning'
    ? selectedProvisioningRequestId
    : (selectedArticleId || selectedProjectId || selectedTicketId || selectedTaskId);
  if (id) p.set('id', id);

  // Page-level detail selections
  if (selectedCustomerClient && view === 'customers') p.set('client', encodeURIComponent(selectedCustomerClient));
  if (expandedFsTicketId && view === 'freshservice') p.set('fst', String(expandedFsTicketId));
  if (selectedDecisionTree && view === 'decisions') p.set('dt', selectedDecisionTree);
  if (expandedProjectTask && TASK_DRAWER_VIEWS.includes(view)) p.set('t', expandedProjectTask);
  if (expandedAuditEntry && (view === 'admin' || view === 'users')) p.set('ae', expandedAuditEntry);

  // Calendar drill-in — Timeline scope inside the Calendar view, or the
  // project-detail follow-on with calendar/timeline breadcrumbs.
  if (calendarDrillProjectId && (view === 'calendar' || view === 'projects')) {
    p.set('cdpid', calendarDrillProjectId);
  }

  // KB + folder — persist on KB browsing, article view, and editor so that
  // share links open directly at the right KB and folder.
  if (activeKnowledgeBaseId && (view === 'kb' || view === 'article' || view === 'editor')) {
    p.set('kb', activeKnowledgeBaseId);
  }
  if (selectedCategoryId && view === 'kb') {
    p.set('cat', selectedCategoryId);
  }

  return '#' + p.toString();
}

function getPersistedActiveKb(): string {
  try {
    return localStorage.getItem('mxdr365_active_kb') || 'default';
  } catch { /* ignore */ }
  return 'default';
}

function persistActiveKb(kbId: string) {
  try {
    localStorage.setItem('mxdr365_active_kb', kbId);
  } catch { /* ignore */ }
}

function getPersistedThemeSchedule(): ThemeSchedule {
  try {
    const saved = localStorage.getItem('mxdr365_theme');
    if (saved) return JSON.parse(saved) as ThemeSchedule;
  } catch { /* ignore */ }
  return { mode: 'dark', lightStartTime: '08:00', lightEndTime: '20:00' };
}

function getPersistedFreshServiceConfig(): FreshServiceConfig {
  // Hardcoded defaults — backend has the API key in Cosmos DB, frontend just needs the domain
  const defaults: FreshServiceConfig = {
    domain: 'support.patriotconsulting.com',
    apiKey: '',
    apiKeyConfigured: true,
    enabled: true,
    syncEnabled: true,
    syncIntervalMinutes: 10,
    defaultGroupId: null,
    lastSyncAt: null,
    lastSyncCount: 0,
  };
  try {
    const saved = localStorage.getItem('mxdr365_fs_config');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Restore API key from its own storage key (kept separate so the rest
      // of the config can be serialised without accidentally leaking it in
      // debug tools that dump the whole config object).
      const storedKey = localStorage.getItem('mxdr365_fs_key') || '';
      return { ...defaults, ...parsed, apiKey: storedKey };
    }
  } catch { /* ignore */ }
  return defaults;
}

function resolveActiveTheme(schedule: ThemeSchedule): 'dark' | 'light' {
  if (schedule.mode === 'dark') return 'dark';
  if (schedule.mode === 'light') return 'light';
  return getAutoTheme(schedule.lightStartTime, schedule.lightEndTime);
}

function createInitialState(): AppState {
  const themeSchedule = getPersistedThemeSchedule();
  const hash = readHashState();
  return {
    user: BLANK_USER,
    impersonatingFrom: null,
    originalUser: null,
    view: hash.view ?? getPersistedView(),
    sidebarOpen: true,
    searchOpen: false,
    searchQuery: '',
    categories: getPersistedCategories(),
    articles: initArticles(),
    projects: initProjects(),
    standaloneTasks: [],
    unsupportedProjectTasks: [],
    handoffs: [],
    tickets: initTickets(),
    progress: initProgress(),
    courses: COURSES,
    trainingAssignments: [],
    trainingGroups: [],
    remedialRule: null,
    notifications: initNotifications(),
    escalations: initEscalations(),
    rosterSlots: initRosterSlots(),
    incidentResponses: [],
    selectedIRCaseId: hash.selectedIRCaseId ?? null,
    selectedProvisioningRequestId: hash.selectedProvisioningRequestId ?? null,
    selectedArticleId: hash.selectedArticleId ?? null,
    selectedCategoryId: hash.selectedCategoryId ?? null,
    selectedProjectId: hash.selectedProjectId ?? null,
    selectedTaskId: hash.selectedTaskId ?? null,
    selectedTicketId: hash.selectedTicketId ?? null,
    selectedCustomerClient: hash.selectedCustomerClient ?? null,
    expandedFsTicketId: hash.expandedFsTicketId ?? null,
    selectedDecisionTree: hash.selectedDecisionTree ?? null,
    expandedProjectTask: hash.expandedProjectTask ?? null,
    expandedAuditEntry: hash.expandedAuditEntry ?? null,
    pmMode: hash.pmMode ?? 'table',
    lmsTab: hash.lmsTab ?? 'courses',
    adminTab: hash.adminTab ?? 'overview',
    freshServiceTab: hash.freshServiceTab ?? 'dashboard',
    editorArticleId: null,
    editorCategoryId: null,
    toast: null,
    customRoles: [],
    roleAssignments: [],
    retentionPolicies: DEFAULT_RETENTION_POLICIES,
    freshServiceConfig: getPersistedFreshServiceConfig(),
    freshServiceStats: null,
    freshServiceTickets: [],
    freshServiceAnalytics: null,
    notificationPrefs: DEFAULT_NOTIFICATION_PREFS,
    themeSchedule,
    activeThemeMode: resolveActiveTheme(themeSchedule),
    presence: [],
    onlineUsers: [],
    importJobs: [],
    knowledgeBases: [],
    activeKnowledgeBaseId: hash.activeKnowledgeBaseId ?? getPersistedActiveKb(),
    dbDecisionTrees: [],
    registeredUsers: [],
    sprints: [],
    goals: [],
    projectTemplates: BUILTIN_PROJECT_TEMPLATES,
    savedProjectViews: [],
    projectColumnsOverride: null,
    activeSprintId: null,
    activeSavedViewId: null,
    calendarDrillProjectId: hash.calendarDrillProjectId ?? null,
    traction: {
      rocks: [], metrics: [], issues: [], todos: [], meetings: [], people: [],
      coreValues: [], loaded: false,
    },
    hiring: {
      requisitions: [], candidates: [], interviews: [], loaded: false,
    },
    peopleOps: {
      reviews: [], oneOnOnes: [], wellbeing: [], attrition: [], loaded: false,
    },
    discipline: {
      records: [], loaded: false,
    },
    execReports: {
      reports: [], loaded: false,
    },
  };
}

// ─── Built-in project templates (industry-leading defaults) ───
const BUILTIN_PROJECT_TEMPLATES: import('../types').ProjectTemplate[] = [
  {
    id: 'tpl-soc-onboarding', name: 'SOC Customer Onboarding', icon: '🎯',
    description: 'Standard onboarding workflow for a new MXDR customer',
    category: 'Customer Onboarding', isBuiltIn: true,
    createdAt: new Date().toISOString(),
    customFieldDefs: [
      { id: 'cf_tenant', label: 'Tenant ID', type: 'text', placeholder: 'e.g. contoso.onmicrosoft.com' },
      { id: 'cf_tier', label: 'M365 Tier', type: 'select', options: ['E3', 'E5'] },
      { id: 'cf_sentinel', label: 'Sentinel Enabled', type: 'checkbox' },
    ],
    checklist: [
      { text: 'Customer kickoff call completed' },
      { text: 'Lighthouse delegation confirmed' },
      { text: 'Runbook acknowledged' },
    ],
    milestones: [
      { title: 'Onboarding kickoff', offsetDays: 0 },
      { title: 'Tenant connectivity validated', offsetDays: 7 },
      { title: 'Go-live', offsetDays: 21 },
    ],
    taskTemplates: [
      { id: 'tt1', title: 'Schedule kickoff call', priority: 'high', offsetDays: 1, durationDays: 1, estimatedHours: 1 },
      { id: 'tt2', title: 'Collect customer contacts & escalation matrix', priority: 'high', offsetDays: 3, durationDays: 2, estimatedHours: 2, dependsOnIndexes: [0] },
      { id: 'tt3', title: 'Configure Lighthouse delegation', priority: 'critical', offsetDays: 5, durationDays: 1, estimatedHours: 3, dependsOnIndexes: [1] },
      { id: 'tt4', title: 'Validate Defender data ingestion', priority: 'high', offsetDays: 7, durationDays: 1, estimatedHours: 2, dependsOnIndexes: [2] },
      { id: 'tt5', title: 'Configure Sentinel workspace + analytics rules', priority: 'high', offsetDays: 10, durationDays: 2, estimatedHours: 6, dependsOnIndexes: [2] },
      { id: 'tt6', title: 'Document customer-specific runbook', priority: 'medium', offsetDays: 14, durationDays: 3, estimatedHours: 4 },
      { id: 'tt7', title: 'Tabletop incident exercise', priority: 'medium', offsetDays: 18, durationDays: 1, estimatedHours: 2, dependsOnIndexes: [4, 5] },
      { id: 'tt8', title: 'Go-live signoff', priority: 'critical', offsetDays: 21, durationDays: 0, estimatedHours: 1, isMilestone: true, dependsOnIndexes: [6] },
    ],
  },
  {
    id: 'tpl-incident-response', name: 'Major Incident Response', icon: '🚨',
    description: 'Coordinate a sev-1 incident from detection to post-mortem',
    category: 'Incident Response', isBuiltIn: true,
    createdAt: new Date().toISOString(),
    checklist: [
      { text: 'Bridge opened' }, { text: 'Customer notified' }, { text: 'Containment confirmed' },
    ],
    taskTemplates: [
      { id: 'ir1', title: 'Open incident bridge', priority: 'critical', offsetDays: 0, durationDays: 0, estimatedHours: 0.5 },
      { id: 'ir2', title: 'Triage & severity assessment', priority: 'critical', offsetDays: 0, durationDays: 0, estimatedHours: 1, dependsOnIndexes: [0] },
      { id: 'ir3', title: 'Notify customer per RoE', priority: 'critical', offsetDays: 0, durationDays: 0, estimatedHours: 0.5, dependsOnIndexes: [1] },
      { id: 'ir4', title: 'Contain affected assets', priority: 'critical', offsetDays: 0, durationDays: 1, estimatedHours: 4, dependsOnIndexes: [1] },
      { id: 'ir5', title: 'Eradicate threat', priority: 'high', offsetDays: 1, durationDays: 1, estimatedHours: 4, dependsOnIndexes: [3] },
      { id: 'ir6', title: 'Recover services', priority: 'high', offsetDays: 2, durationDays: 1, estimatedHours: 3, dependsOnIndexes: [4] },
      { id: 'ir7', title: 'Customer-facing summary report', priority: 'high', offsetDays: 3, durationDays: 1, estimatedHours: 2, dependsOnIndexes: [5] },
      { id: 'ir8', title: 'Post-incident review', priority: 'medium', offsetDays: 7, durationDays: 1, estimatedHours: 2, isMilestone: true, dependsOnIndexes: [6] },
    ],
  },
  {
    id: 'tpl-quarterly-review', name: 'Quarterly Customer Review', icon: '📊',
    description: 'Recurring QBR cycle covering metrics, KPIs, and roadmap',
    category: 'Customer Success', isBuiltIn: true,
    createdAt: new Date().toISOString(),
    taskTemplates: [
      { id: 'qbr1', title: 'Pull SLA + incident metrics', priority: 'high', offsetDays: 7, durationDays: 2, estimatedHours: 3 },
      { id: 'qbr2', title: 'Draft executive summary', priority: 'high', offsetDays: 10, durationDays: 2, estimatedHours: 4, dependsOnIndexes: [0] },
      { id: 'qbr3', title: 'Internal review', priority: 'medium', offsetDays: 12, durationDays: 1, estimatedHours: 1, dependsOnIndexes: [1] },
      { id: 'qbr4', title: 'Schedule QBR with customer', priority: 'medium', offsetDays: 5, durationDays: 1, estimatedHours: 0.5 },
      { id: 'qbr5', title: 'Conduct QBR meeting', priority: 'high', offsetDays: 14, durationDays: 0, estimatedHours: 1.5, isMilestone: true, dependsOnIndexes: [2, 3] },
      { id: 'qbr6', title: 'Action items follow-up', priority: 'medium', offsetDays: 21, durationDays: 5, estimatedHours: 4, dependsOnIndexes: [4] },
    ],
  },
  {
    id: 'tpl-kb-publish', name: 'Knowledge Article Publishing', icon: '📚',
    description: 'Author, review, and publish a new KB article',
    category: 'Knowledge', isBuiltIn: true,
    createdAt: new Date().toISOString(),
    taskTemplates: [
      { id: 'kb1', title: 'Draft outline', priority: 'medium', offsetDays: 1, durationDays: 1, estimatedHours: 1 },
      { id: 'kb2', title: 'Write content', priority: 'medium', offsetDays: 3, durationDays: 2, estimatedHours: 4, dependsOnIndexes: [0] },
      { id: 'kb3', title: 'Peer review', priority: 'medium', offsetDays: 5, durationDays: 2, estimatedHours: 1, dependsOnIndexes: [1] },
      { id: 'kb4', title: 'SME verification', priority: 'high', offsetDays: 7, durationDays: 1, estimatedHours: 0.5, dependsOnIndexes: [2] },
      { id: 'kb5', title: 'Publish', priority: 'high', offsetDays: 8, durationDays: 0, estimatedHours: 0.5, isMilestone: true, dependsOnIndexes: [3] },
    ],
  },
];

// ─── Article normalizer — ensures array fields are never undefined ───
function normalizeArticle<T extends Partial<Article>>(a: T): T {
  return {
    ...a,
    tags: Array.isArray(a.tags) ? a.tags : [],
    mitreTechniques: Array.isArray(a.mitreTechniques) ? a.mitreTechniques : [],
    relatedArticleIds: Array.isArray(a.relatedArticleIds) ? a.relatedArticleIds : [],
    feedback: a.feedback ?? { up: 0, down: 0 },
  };
}

// ─── Reducer ───
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_USER': {
      const user = state.registeredUsers.find(u => u.id === action.userId) || USERS.find(u => u.id === action.userId);
      if (!user) return state;
      return { ...state, user, impersonatingFrom: null };
    }

    case 'SET_CURRENT_USER':
      // Real identity from Entra ID via MSAL — replaces the mock default user.
      // Only update if we are not currently impersonating (admins can still
      // impersonate from the Admin Panel).
      if (state.impersonatingFrom !== null) return state;
      return { ...state, user: action.user };

    case 'SET_USER_SHIFT':
      // Update the current user's shift; also persisted to /api/me via persistAction.
      return { ...state, user: { ...state.user, shift: action.shift } };

    case 'SWITCH_ROLE':
      // Let admins view the app through a different role lens without losing
      // their real identity. Store the real role in originalUser so we can
      // restore it.  Only admins can switch away from admin.
      if (state.user.role !== 'admin' && !state.originalUser) return state;
      if (action.role === state.user.role) return state;
      return {
        ...state,
        user: { ...state.user, role: action.role },
        originalUser: state.originalUser || state.user,
        // If switching to a restricted role while on a hidden view, go home
        view: isViewAllowed(action.role, state.view) ? state.view : 'home',
      };

    case 'IMPERSONATE_USER': {
      const target = state.registeredUsers.find(u => u.id === action.userId);
      if (!target || target.id === state.user.id) return state;
      return { ...state, user: target, impersonatingFrom: state.user.id, originalUser: state.user };
    }

    case 'STOP_IMPERSONATION': {
      if (!state.impersonatingFrom) return state;
      const original = state.originalUser || state.registeredUsers.find(u => u.id === state.impersonatingFrom);
      if (!original) return state;
      return { ...state, user: original, impersonatingFrom: null, originalUser: null };
    }

    case 'SET_VIEW': {
      // Compute the new selection fields to compare against current state.
      const nextArtId = action.view === 'article' ? (action.entityId || state.selectedArticleId) : state.selectedArticleId;
      const nextCatId = action.view === 'kb' ? (action.entityId || null) : state.selectedCategoryId;
      const nextProjId = action.view === 'projects' && action.entityId ? action.entityId : state.selectedProjectId;
      const nextTaskId = (action.view === 'tasks' || action.view === 'mytasks') && action.entityId ? action.entityId : state.selectedTaskId;
      const nextTicketId = action.view === 'ticket' && action.entityId ? action.entityId : state.selectedTicketId;
      const nextIRId = action.view === 'ir' ? (action.entityId ?? state.selectedIRCaseId) : state.selectedIRCaseId;
      const nextProvId = action.view === 'provisioning' ? (action.entityId ?? state.selectedProvisioningRequestId) : state.selectedProvisioningRequestId;
      const nextAdminTab = action.view === 'users' ? 'users' : state.adminTab;
      const nextFsTab = (action.view === 'tickets' || action.view === 'ticket') ? 'tickets' : state.freshServiceTab;
      // Bail out if nothing actually changes — some onClick handlers fire
      // navigate('kb') even when already on kb view, and the resulting
      // identical state object reference still forces a full provider re-render.
      // Combined with stale-closure dispatches in cascading effects, this can
      // pile up enough renders to trip React error #300 (too many re-renders).
      if (
        state.view === action.view &&
        state.selectedArticleId === nextArtId &&
        state.selectedCategoryId === nextCatId &&
        state.selectedProjectId === nextProjId &&
        state.selectedTaskId === nextTaskId &&
        state.selectedTicketId === nextTicketId &&
        state.selectedIRCaseId === nextIRId &&
        state.selectedProvisioningRequestId === nextProvId &&
        state.adminTab === nextAdminTab &&
        state.freshServiceTab === nextFsTab
      ) {
        return state;
      }
      persistView(action.view);
      // Clear the calendar→timeline drill any time the user leaves the
      // calendar/projects chain entirely. Navigating between calendar and
      // projects (the legitimate drill path) keeps it; anything else
      // (e.g. clicking a sidebar entry to KB, Tasks, etc.) drops it so the
      // breadcrumbs don't reappear unexpectedly later.
      const nextDrill = (action.view === 'calendar' || action.view === 'projects')
        ? state.calendarDrillProjectId
        : null;
      return {
        ...state,
        view: action.view,
        selectedArticleId: nextArtId,
        selectedCategoryId: nextCatId,
        selectedProjectId: nextProjId,
        selectedTaskId: nextTaskId,
        selectedTicketId: nextTicketId,
        selectedIRCaseId: nextIRId,
        selectedProvisioningRequestId: nextProvId,
        adminTab: nextAdminTab,
        freshServiceTab: nextFsTab,
        calendarDrillProjectId: nextDrill,
      };
    }

    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarOpen: !state.sidebarOpen };

    case 'SET_SIDEBAR':
      return { ...state, sidebarOpen: action.open };

    case 'SET_SEARCH_OPEN':
      return { ...state, searchOpen: action.open, searchQuery: action.open ? state.searchQuery : '' };

    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.query };

    case 'UPDATE_ARTICLE': {
      const articles = state.articles.map(a => a.id === action.article.id ? action.article : a);
      return { ...state, articles };
    }

    case 'MOVE_ARTICLES': {
      const idSet = new Set(action.articleIds);
      const articles = state.articles.map(a =>
        idSet.has(a.id) ? { ...a, categoryId: action.targetCategoryId } : a,
      );
      return { ...state, articles };
    }

    case 'CREATE_ARTICLE': {
      return { ...state, articles: [action.article, ...state.articles] };
    }

    case 'VOTE_ARTICLE': {
      const articles = state.articles.map(a => {
        if (a.id !== action.articleId) return a;
        return { ...a, feedback: { ...a.feedback, [action.vote]: a.feedback[action.vote] + 1 } };
      });
      return { ...state, articles };
    }

    case 'VERIFY_ARTICLE': {
      const now = new Date().toISOString().slice(0, 10);
      const articles = state.articles.map(a => {
        if (a.id !== action.articleId) return a;
        return { ...a, verifiedAt: now, updatedAt: now };
      });
      return { ...state, articles, toast: '✅ Article verified' };
    }

    case 'BULK_VERIFY_ARTICLES': {
      const now = new Date().toISOString().slice(0, 10);
      const idSet = new Set(action.articleIds);
      const articles = state.articles.map(a => {
        if (!idSet.has(a.id)) return a;
        return { ...a, verifiedAt: now, updatedAt: now };
      });
      return { ...state, articles, toast: `✅ Verified ${action.articleIds.length} articles` };
    }

    case 'SET_PM_MODE':
      return { ...state, pmMode: action.mode };

    case 'CALENDAR_DRILL_INTO_PROJECT':
      return { ...state, calendarDrillProjectId: action.projectId };
    case 'CALENDAR_DRILL_CLEAR':
      return { ...state, calendarDrillProjectId: null };

    case 'SET_LMS_TAB':
      return { ...state, lmsTab: action.tab };

    case 'SET_ADMIN_TAB':
      return { ...state, adminTab: action.tab };
    case 'SET_TRACTION_DATA':
      return { ...state, traction: { ...state.traction, ...action.data } };
    case 'TRACTION_UPSERT': {
      const list = state.traction[action.kind] as Array<{ id: string }>;
      const next = list.some(x => x.id === action.item.id)
        ? list.map(x => (x.id === action.item.id ? action.item : x))
        : action.insertAt === 'start' ? [action.item, ...list] : [...list, action.item];
      return { ...state, traction: { ...state.traction, [action.kind]: next } as GmTractionState };
    }
    case 'TRACTION_REMOVE': {
      const list = state.traction[action.kind] as Array<{ id: string }>;
      return { ...state, traction: { ...state.traction, [action.kind]: list.filter(x => !action.ids.includes(x.id)) } as GmTractionState };
    }
    case 'SET_HIRING_DATA':
      return { ...state, hiring: { ...state.hiring, ...action.data } };
    case 'HIRING_UPSERT': {
      const list = state.hiring[action.kind] as Array<{ id: string }>;
      const next = list.some(x => x.id === action.item.id)
        ? list.map(x => (x.id === action.item.id ? action.item : x))
        : action.insertAt === 'start' ? [action.item, ...list] : [...list, action.item];
      return { ...state, hiring: { ...state.hiring, [action.kind]: next } as GmHiringState };
    }
    case 'HIRING_REMOVE': {
      const list = state.hiring[action.kind] as Array<{ id: string }>;
      return { ...state, hiring: { ...state.hiring, [action.kind]: list.filter(x => !action.ids.includes(x.id)) } as GmHiringState };
    }
    case 'SET_PEOPLE_DATA':
      return { ...state, peopleOps: { ...state.peopleOps, ...action.data } };
    case 'PEOPLE_UPSERT': {
      const list = state.peopleOps[action.kind] as Array<{ id: string }>;
      const next = list.some(x => x.id === action.item.id)
        ? list.map(x => (x.id === action.item.id ? action.item : x))
        : action.insertAt === 'start' ? [action.item, ...list] : [...list, action.item];
      return { ...state, peopleOps: { ...state.peopleOps, [action.kind]: next } as GmPeopleState };
    }
    case 'PEOPLE_REMOVE': {
      const list = state.peopleOps[action.kind] as Array<{ id: string }>;
      return { ...state, peopleOps: { ...state.peopleOps, [action.kind]: list.filter(x => !action.ids.includes(x.id)) } as GmPeopleState };
    }
    case 'SET_DISCIPLINE_DATA':
      return { ...state, discipline: { ...state.discipline, ...action.data } };
    case 'DISCIPLINE_UPSERT': {
      const list = state.discipline[action.kind] as Array<{ id: string }>;
      const next = list.some(x => x.id === action.item.id)
        ? list.map(x => (x.id === action.item.id ? action.item : x))
        : action.insertAt === 'start' ? [action.item, ...list] : [...list, action.item];
      return { ...state, discipline: { ...state.discipline, [action.kind]: next } as GmDisciplineState };
    }
    case 'DISCIPLINE_REMOVE': {
      const list = state.discipline[action.kind] as Array<{ id: string }>;
      return { ...state, discipline: { ...state.discipline, [action.kind]: list.filter(x => !action.ids.includes(x.id)) } as GmDisciplineState };
    }
    case 'SET_EXEC_REPORTS_DATA':
      return { ...state, execReports: { ...state.execReports, ...action.data } };
    case 'EXEC_REPORTS_UPSERT': {
      const list = state.execReports[action.kind] as Array<{ id: string }>;
      const next = list.some(x => x.id === action.item.id)
        ? list.map(x => (x.id === action.item.id ? action.item : x))
        : action.insertAt === 'start' ? [action.item, ...list] : [...list, action.item];
      return { ...state, execReports: { ...state.execReports, [action.kind]: next } as GmExecReportsState };
    }
    case 'EXEC_REPORTS_REMOVE': {
      const list = state.execReports[action.kind] as Array<{ id: string }>;
      return { ...state, execReports: { ...state.execReports, [action.kind]: list.filter(x => !action.ids.includes(x.id)) } as GmExecReportsState };
    }

    case 'SET_FS_TAB':
      return { ...state, freshServiceTab: action.tab };

    case 'CYCLE_TASK_STATUS': {
      const order: TaskStatus[] = ['todo', 'progress', 'review', 'done', 'blocked', 'off-track'];
      const cycle = (t: Task): Task => {
        if (t.id !== action.taskId) return t;
        const nextIdx = (order.indexOf(t.status) + 1) % order.length;
        return { ...t, status: order[nextIdx], completedAt: order[nextIdx] === 'done' ? new Date().toISOString() : null };
      };
      const projects = state.projects.map(p => ({ ...p, tasks: p.tasks.map(cycle) }));
      const standaloneTasks = state.standaloneTasks.map(cycle);
      return { ...state, projects, standaloneTasks, toast: '✅ Status updated' };
    }

    case 'UPDATE_TASK': {
      // Repositioning: if the patch carries a `projectId` that differs from
      // the task's current bucket, lift the task out of the old bucket
      // (project tasks or standaloneTasks) and re-insert into the new one
      // with the patch applied.
      const id = action.task.id;
      let current: Task | undefined;
      for (const p of state.projects) {
        const found = p.tasks.find(t => t.id === id);
        if (found) { current = found; break; }
      }
      if (!current) current = state.standaloneTasks.find(t => t.id === id);

      const targetProjectId = action.task.projectId === undefined ? (current?.projectId ?? null) : action.task.projectId;
      const movingBuckets = !!current && current.projectId !== targetProjectId;

      if (!movingBuckets) {
        // In-place merge — current bucket stays.
        const projects = state.projects.map(p => ({
          ...p,
          tasks: p.tasks.map(t => t.id === id ? { ...t, ...action.task } : t),
        }));
        const standaloneTasks = state.standaloneTasks.map(t => t.id === id ? { ...t, ...action.task } : t);
        return { ...state, projects, standaloneTasks };
      }

      // Cross-bucket move: remove from wherever it lives now and insert in
      // the new bucket with the patch applied.
      const merged: Task = { ...(current as Task), ...action.task, projectId: targetProjectId };
      const projectsStripped = state.projects.map(p => ({
        ...p,
        tasks: p.tasks.filter(t => t.id !== id),
      }));
      const standaloneStripped = state.standaloneTasks.filter(t => t.id !== id);
      if (targetProjectId === null) {
        return { ...state, projects: projectsStripped, standaloneTasks: [...standaloneStripped, merged] };
      }
      const projects = projectsStripped.map(p =>
        p.id === targetProjectId ? { ...p, tasks: [...p.tasks, merged] } : p,
      );
      return { ...state, projects, standaloneTasks: standaloneStripped };
    }

    case 'ADD_TASK_COMMENT': {
      const stamp = new Date().toISOString();
      const appendComment = (t: Task): Task => {
        if (t.id !== action.taskId) return t;
        return {
          ...t,
          comments: [...t.comments, { by: state.user.id, t: action.comment, at: stamp }],
        };
      };
      const projects = state.projects.map(p => ({ ...p, tasks: p.tasks.map(appendComment) }));
      const standaloneTasks = state.standaloneTasks.map(appendComment);
      return { ...state, projects, standaloneTasks, toast: '💬 Comment posted' };
    }

    case 'COMPLETE_MODULE': {
      const { userId, courseId, moduleId, score } = action;
      const key = userId;
      const userProgress = [...(state.progress[key] || [])];
      let courseProgress = userProgress.find(p => p.courseId === courseId);
      const course = state.courses.find(c => c.id === courseId);
      if (!course) return state;

      if (!courseProgress) {
        courseProgress = {
          id: `${userId}_${courseId}`, userId, courseId, status: 'in-progress',
          completionPercent: 0, assessmentScore: null, moduleProgress: {},
          lastActiveAt: new Date().toISOString(), completedAt: null, totalTimeMinutes: 0, certificateGenerated: false,
        };
        userProgress.push(courseProgress);
      }

      courseProgress.moduleProgress[moduleId] = {
        status: 'completed', score, timeSpentMinutes: 25,
        completedAt: new Date().toISOString(), scormData: {}, bookmark: null,
      };

      const completedCount = Object.values(courseProgress.moduleProgress).filter(m => m.status === 'completed').length;
      courseProgress.completionPercent = Math.round((completedCount / course.modules.length) * 100);
      if (courseProgress.completionPercent === 100) {
        courseProgress.status = 'completed';
        courseProgress.completedAt = new Date().toISOString();
      } else {
        courseProgress.status = 'in-progress';
      }

      const module = course.modules.find(m => m.id === moduleId);
      if (module?.type === 'assessment' && score !== null) {
        courseProgress.assessmentScore = score;
      }

      return {
        ...state,
        progress: { ...state.progress, [key]: userProgress },
        toast: `✅ ${module?.title || 'Module'} completed`,
      };
    }

    // ─── Ticket actions ───
    case 'CREATE_TICKET': {
      return { ...state, tickets: [action.ticket, ...state.tickets], toast: '🎫 Ticket created' };
    }

    case 'UPDATE_TICKET': {
      const tickets = state.tickets.map(t => t.id === action.ticket.id ? { ...t, ...action.ticket, updatedAt: new Date().toISOString() } : t);
      return { ...state, tickets };
    }

    case 'SET_TICKET_STATUS': {
      const now = new Date().toISOString();
      const tickets = state.tickets.map(t => {
        if (t.id !== action.ticketId) return t;
        return {
          ...t,
          status: action.status,
          updatedAt: now,
          resolvedAt: action.status === 'resolved' ? now : t.resolvedAt,
          closedAt: action.status === 'closed' ? now : t.closedAt,
        };
      });
      return { ...state, tickets, toast: `🎫 Ticket ${action.status.replace('_', ' ')}` };
    }

    case 'ASSIGN_TICKET': {
      const now = new Date().toISOString();
      const tickets = state.tickets.map(t => {
        if (t.id !== action.ticketId) return t;
        return {
          ...t,
          assigneeId: action.assigneeId,
          status: t.status === 'new' ? 'assigned' as const : t.status,
          updatedAt: now,
          firstResponseAt: t.firstResponseAt || now,
        };
      });
      return { ...state, tickets, toast: '🎫 Ticket assigned' };
    }

    case 'ADD_TICKET_COMMENT': {
      const now = new Date().toISOString();
      const tickets = state.tickets.map(t => {
        if (t.id !== action.ticketId) return t;
        const newComment = {
          id: `tc-${Date.now()}`,
          authorId: state.user.id,
          content: action.content,
          isInternal: action.isInternal,
          attachments: [] as string[],
          createdAt: now,
          editedAt: null,
        };
        const newTimelineEvent: TimelineEvent = {
          id: `tl-${Date.now()}`, type: 'comment', authorId: state.user.id,
          content: action.isInternal ? 'Added internal note' : 'Added reply', createdAt: now,
        };
        return {
          ...t,
          comments: [...t.comments, newComment],
          timeline: [...t.timeline, newTimelineEvent],
          updatedAt: now,
          firstResponseAt: t.firstResponseAt || now,
          lastCustomerReplyAt: action.isInternal ? t.lastCustomerReplyAt : now,
        };
      });
      return { ...state, tickets, toast: action.isInternal ? '📝 Internal note added' : '💬 Reply sent' };
    }

    case 'SELECT_TICKET':
      return { ...state, selectedTicketId: action.id };

    case 'ESCALATE_TICKET': {
      const now = new Date().toISOString();
      const tickets = state.tickets.map(t => {
        if (t.id !== action.ticketId) return t;
        const evt: TimelineEvent = { id: `tl-${Date.now()}`, type: 'escalation', authorId: state.user.id, content: 'Ticket escalated', oldValue: t.status, newValue: 'escalated', createdAt: now };
        return { ...t, status: 'escalated' as TicketStatus, escalationCount: t.escalationCount + 1, timeline: [...t.timeline, evt], updatedAt: now };
      });
      return { ...state, tickets, toast: '🔺 Ticket escalated' };
    }

    case 'MERGE_TICKET': {
      const now = new Date().toISOString();
      const tickets = state.tickets.map(t => {
        if (t.id === action.sourceId) return { ...t, status: 'closed' as TicketStatus, mergedIntoId: action.targetId, closedAt: now, updatedAt: now };
        if (t.id === action.targetId) return { ...t, relatedTicketIds: [...t.relatedTicketIds, action.sourceId], updatedAt: now };
        return t;
      });
      return { ...state, tickets, toast: '🔗 Tickets merged' };
    }

    case 'ADD_WATCHER': {
      const tickets = state.tickets.map(t => {
        if (t.id !== action.ticketId || t.watcherIds.includes(action.userId)) return t;
        return { ...t, watcherIds: [...t.watcherIds, action.userId], updatedAt: new Date().toISOString() };
      });
      return { ...state, tickets, toast: '👁️ Watcher added' };
    }

    case 'REMOVE_WATCHER': {
      const tickets = state.tickets.map(t => {
        if (t.id !== action.ticketId) return t;
        return { ...t, watcherIds: t.watcherIds.filter(id => id !== action.userId), updatedAt: new Date().toISOString() };
      });
      return { ...state, tickets };
    }

    case 'LOG_TIME': {
      const now = new Date().toISOString();
      const tickets = state.tickets.map(t => {
        if (t.id !== action.ticketId) return t;
        const entry = { id: `te-${Date.now()}`, userId: state.user.id, minutes: action.minutes, billable: action.billable, note: action.note, createdAt: now };
        const evt: TimelineEvent = { id: `tl-${Date.now()}`, type: 'time_logged', authorId: state.user.id, content: `Logged ${action.minutes}m${action.billable ? ' (billable)' : ''}`, createdAt: now };
        return { ...t, timeEntries: [...t.timeEntries, entry], totalTimeMinutes: t.totalTimeMinutes + action.minutes, timeline: [...t.timeline, evt], updatedAt: now };
      });
      return { ...state, tickets, toast: `⏱️ ${action.minutes}m logged` };
    }

    case 'APPROVE_TICKET': {
      const now = new Date().toISOString();
      const tickets = state.tickets.map(t => {
        if (t.id !== action.ticketId) return t;
        const approvals = t.approvals.map(a => a.approverId === action.approverId ? { ...a, status: action.status, comment: action.comment, decidedAt: now } : a);
        const allApproved = approvals.every(a => a.status === 'approved');
        const anyRejected = approvals.some(a => a.status === 'rejected');
        const evt: TimelineEvent = { id: `tl-${Date.now()}`, type: 'approval', authorId: action.approverId, content: `${action.status === 'approved' ? 'Approved' : 'Rejected'}: ${action.comment}`, createdAt: now };
        return { ...t, approvals, approvalStatus: (anyRejected ? 'rejected' : allApproved ? 'approved' : 'pending') as ApprovalStatus, timeline: [...t.timeline, evt], updatedAt: now };
      });
      return { ...state, tickets, toast: action.status === 'approved' ? '✅ Approved' : '❌ Rejected' };
    }

    case 'ADD_TIMELINE_EVENT': {
      const tickets = state.tickets.map(t => {
        if (t.id !== action.ticketId) return t;
        return { ...t, timeline: [...t.timeline, action.event], updatedAt: new Date().toISOString() };
      });
      return { ...state, tickets };
    }

    case 'RATE_TICKET': {
      const tickets = state.tickets.map(t => {
        if (t.id !== action.ticketId) return t;
        return { ...t, csatScore: action.score, csatComment: action.comment, updatedAt: new Date().toISOString() };
      });
      return { ...state, tickets, toast: '⭐ Rating submitted' };
    }

    case 'READ_NOTIFICATION': {
      const notifications = state.notifications.map(n => n.id === action.notifId ? { ...n, read: true } : n);
      return { ...state, notifications };
    }

    case 'READ_ALL_NOTIFICATIONS': {
      const notifications = state.notifications.map(n => ({ ...n, read: true }));
      return { ...state, notifications };
    }

    case 'TOAST':
      return { ...state, toast: action.message };

    case 'SELECT_ARTICLE':
      return { ...state, selectedArticleId: action.id };

    case 'SELECT_PROJECT':
      return { ...state, selectedProjectId: action.id };

    case 'SELECT_TASK':
      return { ...state, selectedTaskId: action.id };

    case 'SELECT_CUSTOMER_CLIENT':
      return { ...state, selectedCustomerClient: action.name };

    case 'SELECT_FS_TICKET':
      return { ...state, expandedFsTicketId: action.id };

    case 'SELECT_DECISION_TREE':
      return { ...state, selectedDecisionTree: action.id };

    case 'SELECT_PROJECT_TASK':
      return { ...state, expandedProjectTask: action.id };

    case 'SELECT_AUDIT_ENTRY':
      return { ...state, expandedAuditEntry: action.id };

    case 'OPEN_EDITOR':
      persistView('editor');
      return { ...state, editorArticleId: action.articleId, editorCategoryId: action.categoryId || null, view: 'editor' };

    case 'DELETE_ARTICLE': {
      const articles = state.articles.filter(a => a.id !== action.articleId);
      return { ...state, articles, selectedArticleId: null, view: 'kb', toast: 'Article deleted' };
    }

    case 'CREATE_CATEGORY': {
      const cats = [...state.categories, action.category];
      persistCategories(cats);
      return { ...state, categories: cats, toast: `Category "${action.category.name}" created` };
    }

    case 'DELETE_CATEGORY': {
      const cats = state.categories.filter(c => c.id !== action.categoryId);
      persistCategories(cats);
      return { ...state, categories: cats, toast: 'Category deleted' };
    }

    case 'ADD_ARTICLE_COMMENT': {
      const comment: ArticleComment = {
        id: `cmt_${Date.now()}`,
        articleId: action.articleId,
        authorId: state.user.id,
        content: action.content,
        createdAt: new Date().toISOString(),
      };
      const articles = state.articles.map(a =>
        a.id === action.articleId ? { ...a, comments: [...(a.comments || []), comment] } : a,
      );
      return { ...state, articles };
    }

    case 'DELETE_ARTICLE_COMMENT': {
      const articles = state.articles.map(a =>
        a.id === action.articleId
          ? { ...a, comments: (a.comments || []).filter(c => c.id !== action.commentId) }
          : a,
      );
      return { ...state, articles };
    }

    // ─── Escalation workflow ───
    case 'CREATE_ESCALATION':
      return { ...state, escalations: [action.escalation, ...state.escalations], toast: '🔺 Escalation created' };

    case 'ACKNOWLEDGE_ESCALATION': {
      const now = new Date().toISOString();
      const escalations = state.escalations.map(e =>
        e.id === action.id
          ? { ...e, status: 'acknowledged' as EscalationStatus, acknowledgedAt: now, acknowledgedBy: action.userId, updatedAt: now }
          : e,
      );
      return { ...state, escalations, toast: '👁️ Escalation acknowledged' };
    }

    case 'RESOLVE_ESCALATION': {
      const now = new Date().toISOString();
      const escalations = state.escalations.map(e =>
        e.id === action.id
          ? { ...e, status: 'resolved' as EscalationStatus, resolvedAt: now, resolvedBy: action.userId, updatedAt: now }
          : e,
      );
      return { ...state, escalations, toast: '✅ Escalation resolved' };
    }

    case 'BUMP_SEVERITY': {
      const escalations = state.escalations.map(e => {
        if (e.id !== action.id) return e;
        const newSev = Math.max(1, e.severity - 1) as 1 | 2 | 3 | 4;
        return { ...e, severity: newSev, escalationCount: e.escalationCount + 1, updatedAt: new Date().toISOString() };
      });
      return { ...state, escalations, toast: '⬆️ Severity bumped' };
    }

    case 'ASSIGN_ESCALATION': {
      const escalations = state.escalations.map(e =>
        e.id === action.id ? { ...e, assigneeId: action.userId, updatedAt: new Date().toISOString() } : e,
      );
      return { ...state, escalations, toast: '👤 Escalation assigned' };
    }

    // ─── Incident Response ───
    case 'SET_IR_CASES':
      return { ...state, incidentResponses: action.cases };

    case 'CREATE_IR_CASE':
      return {
        ...state,
        incidentResponses: [action.irCase, ...state.incidentResponses],
        toast: `🚨 IR case ${action.irCase.caseNumber} created`,
      };

    case 'UPDATE_IR_CASE': {
      const now = new Date().toISOString();
      const incidentResponses = state.incidentResponses.map(c =>
        c.id === action.irCase.id ? { ...c, ...action.irCase, updatedAt: now } : c,
      );
      return { ...state, incidentResponses, toast: '✅ IR case updated' };
    }

    case 'REPLACE_IR_CASE': {
      // Full replace — used by polling/refresh to pick up other analysts'
      // changes from Cosmos. UI-only, no API write, no toast.
      const incidentResponses = state.incidentResponses.map(c =>
        c.id === action.irCase.id ? action.irCase : c,
      );
      return { ...state, incidentResponses };
    }

    case 'DELETE_IR_CASE':
      return {
        ...state,
        incidentResponses: state.incidentResponses.filter(c => c.id !== action.id),
        selectedIRCaseId: state.selectedIRCaseId === action.id ? null : state.selectedIRCaseId,
        toast: '🗑️ IR case deleted',
      };

    case 'CLOSE_IR_CASE': {
      const now = new Date().toISOString();
      const incidentResponses = state.incidentResponses.map(c =>
        c.id === action.id
          ? { ...c, status: 'closed' as IRStatus, closedAt: now, closedBy: action.userId, updatedAt: now }
          : c,
      );
      return { ...state, incidentResponses, toast: '🔒 IR case closed' };
    }

    case 'ADD_IR_NOTE': {
      const now = new Date().toISOString();
      const incidentResponses = state.incidentResponses.map(c =>
        c.id === action.caseId
          ? { ...c, investigationNotes: [...c.investigationNotes, action.note], updatedAt: now }
          : c,
      );
      return { ...state, incidentResponses, toast: '📝 Note added' };
    }

    case 'ADD_IR_IOC': {
      const now = new Date().toISOString();
      const incidentResponses = state.incidentResponses.map(c =>
        c.id === action.caseId
          ? { ...c, iocs: [...c.iocs, action.ioc], updatedAt: now }
          : c,
      );
      return { ...state, incidentResponses, toast: '🎯 IOC added' };
    }

    case 'REMOVE_IR_IOC': {
      const now = new Date().toISOString();
      const incidentResponses = state.incidentResponses.map(c =>
        c.id === action.caseId
          ? { ...c, iocs: c.iocs.filter(i => i.id !== action.iocId), updatedAt: now }
          : c,
      );
      return { ...state, incidentResponses };
    }

    case 'ADD_IR_TIMELINE_EVENT': {
      const now = new Date().toISOString();
      const incidentResponses = state.incidentResponses.map(c =>
        c.id === action.caseId
          ? { ...c, timeline: [...c.timeline, action.event], updatedAt: now }
          : c,
      );
      return { ...state, incidentResponses };
    }

    case 'SELECT_IR_CASE':
      return { ...state, selectedIRCaseId: action.id };

    // ─── Roster management ───
    case 'UPDATE_ROSTER_SLOT': {
      const rosterSlots = state.rosterSlots.map(s =>
        s.id === action.slotId ? { ...s, userId: action.userId } : s,
      );
      return { ...state, rosterSlots };
    }

    case 'TOGGLE_SLOT_INACTIVE': {
      const rosterSlots = state.rosterSlots.map(s =>
        s.id === action.slotId ? { ...s, inactive: !s.inactive } : s,
      );
      return { ...state, rosterSlots };
    }

    // ─── RBAC actions ───
    case 'CREATE_CUSTOM_ROLE':
      return { ...state, customRoles: [...state.customRoles, action.role], toast: '✅ Role created' };

    case 'UPDATE_CUSTOM_ROLE':
      return { ...state, customRoles: state.customRoles.map(r => r.id === action.role.id ? action.role : r), toast: '✅ Role updated' };

    case 'DELETE_CUSTOM_ROLE':
      return { ...state, customRoles: state.customRoles.filter(r => r.id !== action.roleId), toast: '🗑️ Role deleted' };

    case 'ASSIGN_ROLE':
      // Dedup by (userId + customRoleId) — a user can have multiple different roles
      return { ...state, roleAssignments: [...state.roleAssignments.filter(a => !(a.userId === action.assignment.userId && a.customRoleId === action.assignment.customRoleId)), action.assignment], toast: '✅ Role assigned' };

    case 'UNASSIGN_ROLE':
      return { ...state, roleAssignments: state.roleAssignments.filter(a => !(a.userId === action.userId && a.customRoleId === action.roleId)) };

    // ─── Retention actions ───
    case 'UPDATE_RETENTION_POLICY':
      return { ...state, retentionPolicies: state.retentionPolicies.map(p => p.id === action.policy.id ? action.policy : p), toast: '✅ Retention policy updated' };

    // ─── FreshService actions ───
    case 'SET_FRESHSERVICE_CONFIG': {
      // Persist config to localStorage. API key goes in a separate key.
      const { apiKey: fsKey, ...safeConfig } = action.config;
      try {
        localStorage.setItem('mxdr365_fs_config', JSON.stringify(safeConfig));
        // Persist API key separately — only overwrite if a real key was provided
        // (not the masked placeholder '••••••••' and not empty from hydration)
        if (fsKey && fsKey !== '••••••••') {
          localStorage.setItem('mxdr365_fs_key', fsKey);
        }
      } catch { /* ignore */ }
      // Only toast when the user explicitly saves settings (has a truthy domain change),
      // not on silent config-status hydration during mount.
      const isUserSave = action.config.domain && action.config.domain !== state.freshServiceConfig.domain;
      return { ...state, freshServiceConfig: action.config, ...(isUserSave ? { toast: '✅ FreshService config saved' } : {}) };
    }
    case 'SET_FRESHSERVICE_STATS':
      return { ...state, freshServiceStats: action.stats };
    case 'SET_FS_TICKETS':
      return { ...state, freshServiceTickets: action.tickets };
    case 'SET_FS_ANALYTICS':
      return { ...state, freshServiceAnalytics: action.analytics };
    case 'ADD_FS_TICKET':
      return { ...state, freshServiceTickets: [action.ticket, ...state.freshServiceTickets] };
    case 'UPDATE_FS_TICKET':
      return { ...state, freshServiceTickets: state.freshServiceTickets.map(t => t.id === action.ticket.id ? action.ticket : t) };

    // ─── Notification prefs ───
    case 'UPDATE_NOTIFICATION_PREFS':
      return { ...state, notificationPrefs: { ...state.notificationPrefs, ...action.prefs, updatedAt: new Date().toISOString() }, toast: '✅ Preferences saved' };

    // ─── Theme actions ───
    case 'SET_THEME_MODE': {
      const schedule = { ...state.themeSchedule, mode: action.mode };
      try { localStorage.setItem('mxdr365_theme', JSON.stringify(schedule)); } catch { /* ignore */ }
      const activeThemeMode = resolveActiveTheme(schedule);
      return { ...state, themeSchedule: schedule, activeThemeMode };
    }
    case 'SET_ACTIVE_THEME':
      return { ...state, activeThemeMode: action.mode };
    case 'UPDATE_THEME_SCHEDULE': {
      const schedule = { ...state.themeSchedule, ...action.schedule };
      try { localStorage.setItem('mxdr365_theme', JSON.stringify(schedule)); } catch { /* ignore */ }
      const activeThemeMode = resolveActiveTheme(schedule);
      return { ...state, themeSchedule: schedule, activeThemeMode };
    }

    // ─── Presence actions ───
    case 'SET_PRESENCE':
      return { ...state, presence: action.users };
    case 'UPDATE_PRESENCE': {
      const existing = state.presence.find(p => p.userId === action.user.userId);
      const presence = existing
        ? state.presence.map(p => p.userId === action.user.userId ? action.user : p)
        : [...state.presence, action.user];
      return { ...state, presence };
    }
    case 'PRUNE_PRESENCE':
      return { ...state, presence: state.presence.filter(p => p.lastSeen > action.cutoff) };
    case 'SET_ONLINE_USERS':
      return { ...state, onlineUsers: action.users };

    // ─── Import actions ───
    case 'ADD_IMPORT_JOB':
      return { ...state, importJobs: [action.job, ...state.importJobs], toast: '📥 Import started' };
    case 'UPDATE_IMPORT_JOB':
      return { ...state, importJobs: state.importJobs.map(j => j.id === action.job.id ? { ...j, ...action.job } : j) };

    // ─── Bulk API load actions ───
    case 'SET_CATEGORIES':
      persistCategories(action.categories);
      return { ...state, categories: action.categories };
    case 'UPDATE_CATEGORY': {
      const exists = state.categories.some(c => c.id === action.category.id);
      const cats = exists
        ? state.categories.map(c => c.id === action.category.id ? { ...c, ...action.category } : c)
        : [...state.categories, action.category as Category];
      persistCategories(cats);
      return { ...state, categories: cats };
    }
    case 'SET_ARTICLES':
      return { ...state, articles: action.articles.map(normalizeArticle) as Article[] };
    case 'SET_ARTICLE_SUMMARIES':
      return { ...state, articles: action.articles.map(normalizeArticle) as Article[] };
    case 'SET_FULL_ARTICLE': {
      const normalized = normalizeArticle(action.article);
      const idx = state.articles.findIndex(a => a.id === normalized.id);
      if (idx >= 0) {
        const articles = [...state.articles];
        articles[idx] = normalized;
        return { ...state, articles };
      }
      return { ...state, articles: [normalized, ...state.articles] };
    }
    case 'SET_PROJECT_DETAIL': {
      const exists = state.projects.some(p => p.id === action.project.id);
      return { ...state, projects: exists
        ? state.projects.map(p => p.id === action.project.id ? action.project : p)
        : [...state.projects, action.project],
        unsupportedProjectTasks: action.unsupportedTasks === undefined ? state.unsupportedProjectTasks : [
          ...(state.unsupportedProjectTasks || []).filter(task => task.projectId !== action.project.id),
          ...action.unsupportedTasks,
        ] };
    }

    case 'SET_PROJECTS':
      return { ...state, projects: action.projects };
    case 'SET_STANDALONE_TASKS':
      return { ...state, standaloneTasks: action.tasks };
    case 'SET_UNSUPPORTED_PROJECT_TASKS':
      return { ...state, unsupportedProjectTasks: action.tasks };
    case 'SET_HANDOFFS':
      return { ...state, handoffs: action.handoffs };
    case 'ADD_HANDOFF':
      return { ...state, handoffs: [action.handoff, ...state.handoffs], toast: '📋 Handoff posted' };
    case 'UPDATE_HANDOFF':
      return { ...state, handoffs: state.handoffs.map(h => h.id === action.handoff.id ? action.handoff : h) };
    case 'ARCHIVE_HANDOFF':
      return { ...state, handoffs: state.handoffs.map(h => h.id === action.handoffId ? { ...h, archived: true } : h), toast: 'Handoff archived' };
    case 'SET_TICKETS':
      return { ...state, tickets: action.tickets };
    case 'SET_ESCALATIONS':
      return { ...state, escalations: action.escalations };
    case 'SET_ROSTER_SLOTS':
      return { ...state, rosterSlots: action.rosterSlots };
    case 'SET_NOTIFICATIONS':
      return { ...state, notifications: action.notifications };

    // ─── Project mutations ───
    case 'CREATE_PROJECT':
      return { ...state, projects: [action.project, ...state.projects], toast: '📋 Project created' };
    case 'UPDATE_PROJECT': {
      const projects = state.projects.map(p => p.id === action.project.id ? { ...p, ...action.project, updatedAt: new Date().toISOString() } : p);
      return { ...state, projects, toast: '✅ Project updated' };
    }
    case 'CREATE_TASK': {
      // Standalone (no project) routes to a separate bucket so existing
      // per-project queries stay untouched.
      const target = action.projectId;
      const normalizedTask: Task = { ...action.task, projectId: target ?? null };
      if (!target) {
        return { ...state, standaloneTasks: [...state.standaloneTasks, normalizedTask], toast: '✅ Task created' };
      }
      const projects = state.projects.map(p => p.id === target ? { ...p, tasks: [...p.tasks, normalizedTask] } : p);
      return { ...state, projects, toast: '✅ Task created' };
    }
    case 'DELETE_TASK': {
      // The caller passes the task's current projectId; for legacy callers
      // that don't know, fall back to a cross-bucket id sweep.
      if (action.projectId === null) {
        return { ...state, standaloneTasks: state.standaloneTasks.filter(t => t.id !== action.taskId), toast: '🗑️ Task deleted' };
      }
      const projects = state.projects.map(p => p.id === action.projectId ? { ...p, tasks: p.tasks.filter(t => t.id !== action.taskId) } : p);
      // If the projectId arg didn't match any project, also try the standalone
      // bucket as a safety net.
      const matched = state.projects.some(p => p.id === action.projectId);
      const standaloneTasks = matched ? state.standaloneTasks : state.standaloneTasks.filter(t => t.id !== action.taskId);
      return { ...state, projects, standaloneTasks, toast: '🗑️ Task deleted' };
    }
    case 'DELETE_PROJECT': {
      const projects = state.projects.filter(p => p.id !== action.projectId);
      return { ...state, projects, toast: '🗑️ Project deleted' };
    }
    case 'ARCHIVE_PROJECT': {
      const projects = state.projects.map(p => p.id === action.projectId ? { ...p, archived: true, updatedAt: new Date().toISOString() } : p);
      return { ...state, projects, toast: '📦 Project archived' };
    }
    case 'RESTORE_PROJECT': {
      const projects = state.projects.map(p => p.id === action.projectId ? { ...p, archived: false, updatedAt: new Date().toISOString() } : p);
      return { ...state, projects, toast: '✅ Project restored' };
    }
    // ─── Kanban columns ───
    case 'SET_PROJECT_COLUMNS_OVERRIDE':
      return { ...state, projectColumnsOverride: action.columns };
    case 'MOVE_PROJECT_TO_COLUMN': {
      const projects = state.projects.map(p =>
        p.id === action.projectId
          ? { ...p, column: action.column, updatedAt: new Date().toISOString() }
          : p,
      );
      return { ...state, projects };
    }
    case 'LOG_TASK_TIME': {
      const now = new Date().toISOString();
      const entry = {
        id: `te-${Date.now()}`,
        userId: state.user.id,
        minutes: action.minutes,
        billable: !!action.billable,
        note: action.note || '',
        workDate: action.workDate || now.slice(0, 10),
        createdAt: now,
      };
      const applyEntry = (t: Task): Task => t.id === action.taskId ? {
        ...t,
        timeEntries: [...(t.timeEntries || []), entry],
        actualHours: (t.actualHours || 0) + action.minutes / 60,
        activity: [...(t.activity || []), { id: `act-${Date.now()}`, type: 'time_logged' as const, userId: state.user.id, text: `logged ${action.minutes}m`, at: now }],
        updatedAt: now,
      } : t;
      const projects = state.projects.map(p =>
        action.projectId !== null && p.id === action.projectId ? { ...p, tasks: p.tasks.map(applyEntry) } : p,
      );
      // Standalone tasks: also walk when projectId is null, OR as fallback if
      // the projectId we got doesn't match a project (legacy callers).
      const matchedProject = action.projectId !== null && state.projects.some(p => p.id === action.projectId);
      const standaloneTasks = matchedProject ? state.standaloneTasks : state.standaloneTasks.map(applyEntry);
      return { ...state, projects, standaloneTasks, toast: `⏱️ ${action.minutes}m logged` };
    }
    case 'DELETE_TASK_TIME': {
      const apply = (t: Task): Task => {
        if (t.id !== action.taskId) return t;
        const entry = (t.timeEntries || []).find(e => e.id === action.entryId);
        return {
          ...t,
          timeEntries: (t.timeEntries || []).filter(e => e.id !== action.entryId),
          actualHours: Math.max(0, (t.actualHours || 0) - (entry ? entry.minutes / 60 : 0)),
          updatedAt: new Date().toISOString(),
        };
      };
      const matchedProject = action.projectId !== null && state.projects.some(p => p.id === action.projectId);
      const projects = state.projects.map(p =>
        action.projectId !== null && p.id === action.projectId ? { ...p, tasks: p.tasks.map(apply) } : p,
      );
      const standaloneTasks = matchedProject ? state.standaloneTasks : state.standaloneTasks.map(apply);
      return { ...state, projects, standaloneTasks };
    }
    case 'ADD_TASK_CHECKLIST': {
      const item = { id: `cl-${Date.now()}`, text: action.text, done: false, doneBy: null, doneAt: null };
      const apply = (t: Task): Task => t.id === action.taskId ? {
        ...t, checklist: [...(t.checklist || []), item], updatedAt: new Date().toISOString(),
      } : t;
      const matchedProject = action.projectId !== null && state.projects.some(p => p.id === action.projectId);
      const projects = state.projects.map(p =>
        action.projectId !== null && p.id === action.projectId ? { ...p, tasks: p.tasks.map(apply) } : p,
      );
      const standaloneTasks = matchedProject ? state.standaloneTasks : state.standaloneTasks.map(apply);
      return { ...state, projects, standaloneTasks };
    }
    case 'TOGGLE_TASK_CHECKLIST': {
      const now = new Date().toISOString();
      const apply = (t: Task): Task => t.id === action.taskId ? {
        ...t,
        checklist: (t.checklist || []).map(c => c.id === action.itemId ? {
          ...c, done: !c.done, doneBy: !c.done ? state.user.id : null, doneAt: !c.done ? now : null,
        } : c),
        updatedAt: now,
      } : t;
      const matchedProject = action.projectId !== null && state.projects.some(p => p.id === action.projectId);
      const projects = state.projects.map(p =>
        action.projectId !== null && p.id === action.projectId ? { ...p, tasks: p.tasks.map(apply) } : p,
      );
      const standaloneTasks = matchedProject ? state.standaloneTasks : state.standaloneTasks.map(apply);
      return { ...state, projects, standaloneTasks };
    }
    case 'REMOVE_TASK_CHECKLIST': {
      const apply = (t: Task): Task => t.id === action.taskId ? {
        ...t, checklist: (t.checklist || []).filter(c => c.id !== action.itemId), updatedAt: new Date().toISOString(),
      } : t;
      const matchedProject = action.projectId !== null && state.projects.some(p => p.id === action.projectId);
      const projects = state.projects.map(p =>
        action.projectId !== null && p.id === action.projectId ? { ...p, tasks: p.tasks.map(apply) } : p,
      );
      const standaloneTasks = matchedProject ? state.standaloneTasks : state.standaloneTasks.map(apply);
      return { ...state, projects, standaloneTasks };
    }
    case 'SET_TASK_DEPENDENCIES': {
      const now = new Date().toISOString();
      // Apply to the bucket the task lives in. Reverse-links are maintained
      // within the same bucket — cross-bucket dependencies are out of scope.
      const applyToBucket = (tasks: Task[]): Task[] => {
        const previous = tasks.find(t => t.id === action.taskId)?.blockedByIds || [];
        return tasks.map(t => {
          if (t.id === action.taskId) {
            return { ...t, blockedByIds: action.blockedByIds, updatedAt: now };
          }
          const wasBlocker = previous.includes(t.id);
          const isBlocker = action.blockedByIds.includes(t.id);
          if (wasBlocker && !isBlocker) {
            return { ...t, blocksTaskIds: (t.blocksTaskIds || []).filter(id => id !== action.taskId) };
          }
          if (!wasBlocker && isBlocker) {
            return { ...t, blocksTaskIds: Array.from(new Set([...(t.blocksTaskIds || []), action.taskId])) };
          }
          return t;
        });
      };
      const matchedProject = action.projectId !== null && state.projects.some(p => p.id === action.projectId);
      const projects = state.projects.map(p =>
        action.projectId !== null && p.id === action.projectId ? { ...p, tasks: applyToBucket(p.tasks) } : p,
      );
      const standaloneTasks = matchedProject ? state.standaloneTasks : applyToBucket(state.standaloneTasks);
      return { ...state, projects, standaloneTasks, toast: '🔗 Dependencies updated' };
    }
    case 'TOGGLE_TASK_WATCHER': {
      const apply = (t: Task): Task => {
        if (t.id !== action.taskId) return t;
        const has = (t.watcherIds || []).includes(action.userId);
        return {
          ...t,
          watcherIds: has
            ? (t.watcherIds || []).filter(id => id !== action.userId)
            : [...(t.watcherIds || []), action.userId],
          updatedAt: new Date().toISOString(),
        };
      };
      const matchedProject = action.projectId !== null && state.projects.some(p => p.id === action.projectId);
      const projects = state.projects.map(p =>
        action.projectId !== null && p.id === action.projectId ? { ...p, tasks: p.tasks.map(apply) } : p,
      );
      const standaloneTasks = matchedProject ? state.standaloneTasks : state.standaloneTasks.map(apply);
      return { ...state, projects, standaloneTasks };
    }
    case 'BULK_UPDATE_TASKS': {
      const now = new Date().toISOString();
      const idSet = new Set(action.taskIds);
      const apply = (t: Task) => idSet.has(t.id) ? { ...t, ...action.updates, updatedAt: now } : t;
      const projects = state.projects.map(p => ({ ...p, tasks: p.tasks.map(apply) }));
      const standaloneTasks = state.standaloneTasks.map(apply);
      return { ...state, projects, standaloneTasks, toast: `✏️ Updated ${action.taskIds.length} tasks` };
    }
    case 'BULK_ARCHIVE_TASKS': {
      const idSet = new Set(action.taskIds);
      const now = new Date().toISOString();
      const apply = (t: Task) => idSet.has(t.id) ? { ...t, archived: true, updatedAt: now } : t;
      const projects = state.projects.map(p => ({ ...p, tasks: p.tasks.map(apply) }));
      const standaloneTasks = state.standaloneTasks.map(apply);
      return { ...state, projects, standaloneTasks, toast: `📦 Archived ${action.taskIds.length} tasks` };
    }
    case 'ADD_PROJECT_STATUS_UPDATE': {
      const now = new Date().toISOString();
      const update = { id: `su-${Date.now()}`, ...action.update, at: now };
      const projects = state.projects.map(p => p.id === action.projectId ? {
        ...p,
        statusUpdates: [update, ...(p.statusUpdates || [])],
        health: update.health,
        updatedAt: now,
      } : p);
      return { ...state, projects, toast: '📣 Status update posted' };
    }
    case 'DELETE_PROJECT_STATUS_UPDATE': {
      const projects = state.projects.map(p => p.id === action.projectId ? {
        ...p,
        statusUpdates: (p.statusUpdates || []).filter(u => u.id !== action.updateId),
      } : p);
      return { ...state, projects, toast: '🗑️ Status update deleted' };
    }
    case 'CREATE_SUBTASK': {
      const child: Task = { ...action.task, parentTaskId: action.parentTaskId, projectId: action.projectId ?? null };
      if (!action.projectId) {
        return { ...state, standaloneTasks: [...state.standaloneTasks, child], toast: '➕ Subtask added' };
      }
      const projects = state.projects.map(p => p.id === action.projectId ? {
        ...p, tasks: [...p.tasks, child],
      } : p);
      return { ...state, projects, toast: '➕ Subtask added' };
    }
    case 'CREATE_SPRINT':
      return { ...state, sprints: [action.sprint, ...state.sprints], toast: '🏁 Sprint created' };
    case 'UPDATE_SPRINT':
      return { ...state, sprints: state.sprints.map(s => s.id === action.sprintId ? { ...s, ...action.updates } : s) };
    case 'DELETE_SPRINT':
      return { ...state, sprints: state.sprints.filter(s => s.id !== action.sprintId), toast: '🗑️ Sprint deleted' };
    case 'SET_SPRINTS':
      return { ...state, sprints: action.sprints };
    case 'CREATE_GOAL':
      return { ...state, goals: [action.goal, ...state.goals], toast: '🎯 Goal created' };
    case 'UPDATE_GOAL':
      return { ...state, goals: state.goals.map(g => g.id === action.goalId ? { ...g, ...action.updates, updatedAt: new Date().toISOString() } : g) };
    case 'DELETE_GOAL':
      return { ...state, goals: state.goals.filter(g => g.id !== action.goalId), toast: '🗑️ Goal deleted' };
    case 'SET_GOALS':
      return { ...state, goals: action.goals };
    case 'CREATE_TEMPLATE':
      return { ...state, projectTemplates: [action.template, ...state.projectTemplates], toast: '🧩 Template saved' };
    case 'DELETE_TEMPLATE':
      return { ...state, projectTemplates: state.projectTemplates.filter(t => t.id !== action.templateId) };
    case 'SET_TEMPLATES':
      // Merge built-ins + remote, dedupe by id
      const seen = new Set<string>();
      const merged = [...action.templates, ...state.projectTemplates].filter(t => {
        if (seen.has(t.id)) return false;
        seen.add(t.id); return true;
      });
      return { ...state, projectTemplates: merged };
    case 'CREATE_PROJECT_VIEW':
      return { ...state, savedProjectViews: [action.view, ...state.savedProjectViews] };
    case 'UPDATE_PROJECT_VIEW':
      return { ...state, savedProjectViews: state.savedProjectViews.map(v => v.id === action.viewId ? { ...v, ...action.updates } : v) };
    case 'DELETE_PROJECT_VIEW':
      return { ...state, savedProjectViews: state.savedProjectViews.filter(v => v.id !== action.viewId) };
    case 'SET_PROJECT_VIEWS':
      return { ...state, savedProjectViews: action.views };

    // ─── Knowledge Bases ───
    case 'SET_KNOWLEDGE_BASES':
      return { ...state, knowledgeBases: action.kbs };
    case 'SET_ACTIVE_KB':
      // No-op when the KB is already active. Prevents unnecessary AppProvider
      // re-renders from sidebar handlers that fire SET_ACTIVE_KB on every
      // click even when the user is already on that KB.
      if (state.activeKnowledgeBaseId === action.kbId) return state;
      persistActiveKb(action.kbId);
      return { ...state, activeKnowledgeBaseId: action.kbId };

    // ─── Decision Trees ───
    case 'SET_DECISION_TREES':
      return { ...state, dbDecisionTrees: action.trees };
    case 'ADD_DECISION_TREE':
      return { ...state, dbDecisionTrees: [...state.dbDecisionTrees, action.tree] };
    case 'UPDATE_DECISION_TREE':
      return { ...state, dbDecisionTrees: state.dbDecisionTrees.map(t => t.id === action.tree.id ? action.tree : t) };
    case 'DELETE_DECISION_TREE':
      return { ...state, dbDecisionTrees: state.dbDecisionTrees.filter(t => t.id !== action.treeId) };

    // ─── Bulk loads: RBAC, training ───
    case 'SET_CUSTOM_ROLES':
      return { ...state, customRoles: action.roles };
    case 'SET_ROLE_ASSIGNMENTS':
      return { ...state, roleAssignments: action.assignments };
    case 'SET_TRAINING_PROGRESS':
      return { ...state, progress: action.progress };
    case 'SET_COURSES':
      return { ...state, courses: action.courses };
    case 'ADD_COURSE': {
      // Upsert — re-uploading the same package replaces the existing entry.
      const others = state.courses.filter(c => c.id !== action.course.id);
      return { ...state, courses: [action.course, ...others], toast: `📦 Course "${action.course.title}" added` };
    }
    case 'UPDATE_COURSE': {
      const courses = state.courses.map(c => c.id === action.course.id ? action.course : c);
      return { ...state, courses, toast: '💾 Course settings saved' };
    }
    case 'SET_TRAINING_ASSIGNMENTS':
      return { ...state, trainingAssignments: action.assignments };
    case 'UPSERT_TRAINING_ASSIGNMENT': {
      const others = state.trainingAssignments.filter(a => a.id !== action.assignment.id);
      return { ...state, trainingAssignments: [action.assignment, ...others], toast: '🎯 Assignment saved' };
    }
    case 'DELETE_TRAINING_ASSIGNMENT':
      return {
        ...state,
        trainingAssignments: state.trainingAssignments.filter(a => a.id !== action.assignmentId),
        toast: '🗑️ Assignment removed',
      };
    case 'SET_TRAINING_GROUPS':
      return { ...state, trainingGroups: action.groups };
    case 'UPSERT_TRAINING_GROUP': {
      const others = state.trainingGroups.filter(g => g.id !== action.group.id);
      return { ...state, trainingGroups: [...others, action.group].sort((a, b) => a.name.localeCompare(b.name)), toast: `👥 Group "${action.group.name}" saved` };
    }
    case 'DELETE_TRAINING_GROUP':
      return {
        ...state,
        trainingGroups: state.trainingGroups.filter(g => g.id !== action.groupId),
        trainingAssignments: state.trainingAssignments.filter(a => a.groupId !== action.groupId),
        toast: '🗑️ Group removed',
      };
    case 'SET_REMEDIAL_RULE':
      return { ...state, remedialRule: action.rule };
    case 'UPSERT_PROGRESS': {
      const key = action.progress.userId;
      const userProgress = (state.progress[key] || []).filter(p => p.courseId !== action.progress.courseId);
      return { ...state, progress: { ...state.progress, [key]: [...userProgress, action.progress] } };
    }

    // ─── Registered Users ───
    case 'SET_REGISTERED_USERS':
      return { ...state, registeredUsers: action.users };
    case 'UPDATE_REGISTERED_USER':
      return { ...state, registeredUsers: state.registeredUsers.map(u => u.id === action.userId ? { ...u, ...action.updates } : u), toast: 'User updated' };
    case 'REMOVE_REGISTERED_USER':
      return { ...state, registeredUsers: state.registeredUsers.filter(u => u.id !== action.userId), toast: 'User removed' };

    default:
      return state;
  }
}

// ─── Context ───
interface AppContextValue {
  state: AppState;
  dispatch: Dispatch<AppAction>;
  // Convenience helpers
  isAdmin: boolean;
  isLead: boolean;
  canEdit: boolean;
  isImpersonating: boolean;
  navigate: (view: ViewId, entityId?: string) => void;
  toast: (msg: string) => void;
  toastError: (message: string, code?: string) => void;
  getUser: (id: string) => User | undefined;
  getAllTasks: () => Task[];
  getMyTasks: () => Task[];
  /** Call API first, then update local state only on success. Throws on failure. */
  saveAction: (action: AppAction) => Promise<any>;
  /** Re-fetch all articles from API and replace local state */
  refreshArticles: () => Promise<void>;
  // RBAC view permission helpers
  canEditKb: (kbId: string) => boolean;
  /** Check if current user can edit entities in a given view (via built-in role or custom RBAC role). */
  canEditView: (viewId: string) => boolean;
  /** Check if current user can delete entities in a given view (via built-in role or custom RBAC role). */
  canDeleteView: (viewId: string) => boolean;
  /** Check if any custom RBAC role grants explicit canView for a viewId — augments base feature-visibility. */
  canViewView: (viewId: string) => boolean;
  // Theme
  currentTheme: typeof darkTheme;
}

// UI-only action types that intentionally don't need API persistence
const UI_ONLY_ACTIONS = new Set([
  'SET_VIEW', 'TOGGLE_SIDEBAR', 'SET_SIDEBAR',
  'SET_SEARCH_OPEN', 'SET_SEARCH_QUERY',
  'SET_PM_MODE', 'SET_LMS_TAB', 'SET_ADMIN_TAB', 'SET_FS_TAB',
  'TOAST', 'SELECT_ARTICLE', 'SELECT_PROJECT', 'SELECT_TASK', 'SELECT_TICKET',
  'SELECT_IR_CASE',
  'OPEN_EDITOR',
  'SET_THEME_MODE', 'SET_ACTIVE_THEME', 'UPDATE_THEME_SCHEDULE',
  'SET_PRESENCE', 'UPDATE_PRESENCE',
  'SET_USER', 'SET_CURRENT_USER', 'IMPERSONATE_USER', 'STOP_IMPERSONATION',
  // Bulk-load actions (data arrives FROM the API, not sent TO it)
  'SET_ARTICLES', 'SET_PROJECTS', 'SET_PROJECT_DETAIL', 'SET_STANDALONE_TASKS', 'SET_UNSUPPORTED_PROJECT_TASKS', 'SET_HANDOFFS', 'SET_TICKETS',
  'SET_ESCALATIONS', 'SET_ROSTER_SLOTS', 'SET_NOTIFICATIONS', 'SET_CATEGORIES',
  'SET_IR_CASES', 'REPLACE_IR_CASE',
  'SET_KNOWLEDGE_BASES', 'SET_ACTIVE_KB', 'SET_CUSTOM_ROLES',
  'SET_ROLE_ASSIGNMENTS', 'SET_TRAINING_PROGRESS', 'SET_COURSES', 'ADD_COURSE', 'UPDATE_COURSE',
  'SET_TRAINING_ASSIGNMENTS', 'UPSERT_TRAINING_ASSIGNMENT', 'DELETE_TRAINING_ASSIGNMENT',
  'SET_TRAINING_GROUPS', 'UPSERT_TRAINING_GROUP', 'DELETE_TRAINING_GROUP',
  'SET_REMEDIAL_RULE', 'UPSERT_PROGRESS', 'SET_DECISION_TREES',
  'SET_FRESHSERVICE_CONFIG', 'SET_FRESHSERVICE_STATS',
  'SET_FS_TICKETS', 'SET_FS_ANALYTICS',
  'ADD_FS_TICKET', 'UPDATE_FS_TICKET',
  'ADD_IMPORT_JOB', 'UPDATE_IMPORT_JOB',
  // RBAC: AdminPage handles API calls directly (API-first, dispatch on success)
  // so wrappedDispatch must NOT also fire persistAction for these.
  'CREATE_CUSTOM_ROLE', 'UPDATE_CUSTOM_ROLE', 'DELETE_CUSTOM_ROLE',
  'ASSIGN_ROLE', 'UNASSIGN_ROLE',
  // Enhanced PM bulk-loads (data flows IN from API)
  'SET_SPRINTS', 'SET_GOALS', 'SET_TEMPLATES', 'SET_PROJECT_VIEWS',
  // Kanban override list — ManageColumns saves directly via api.projectColumns.save()
  // so the SET dispatch is purely a local cache refresh and must NOT re-persist.
  // MOVE_PROJECT_TO_COLUMN still persists below (it patches the project).
  'SET_PROJECT_COLUMNS_OVERRIDE',
]);

// ─── API persistence side-effects for mutations ───
// Returns the API response for the small subset of actions that need it
// post-persist (currently: project/task assignment mutations, so the caller
// can surface `_teamsNotify` warnings in a toast). All other actions return
// undefined.
async function persistAction(action: AppAction, currentUserId: string): Promise<any | void> {
  if (UI_ONLY_ACTIONS.has(action.type)) return;
  switch (action.type) {
    // Articles
    case 'CREATE_ARTICLE': await api.articles.create({ ...action.article, verifiedAt: action.article.verifiedAt ?? undefined }); break;
    case 'UPDATE_ARTICLE': await api.articles.update(action.article.id, { ...action.article, verifiedAt: action.article.verifiedAt ?? undefined }); break;
    case 'DELETE_ARTICLE': await api.articles.delete(action.articleId); break;
    case 'VERIFY_ARTICLE': await api.articles.verify(action.articleId); break;
    case 'BULK_VERIFY_ARTICLES': await api.articles.bulkVerify(action.articleIds); break;
    case 'VOTE_ARTICLE': await api.articles.feedback(action.articleId, action.vote); break;
    case 'ADD_ARTICLE_COMMENT': await api.articles.addComment(action.articleId, action.content); break;
    case 'DELETE_ARTICLE_COMMENT': await api.articles.deleteComment(action.articleId, action.commentId); break;
    // Projects
    case 'CREATE_PROJECT': return api.projects.create({ ...action.project, dueDate: action.project.dueDate ?? undefined });
    case 'UPDATE_PROJECT': return api.projects.update(action.project.id, { ...action.project, dueDate: action.project.dueDate ?? undefined });
    // Tasks
    case 'UPDATE_TASK': return api.tasks.update(action.task.id, action.task);
    case 'CYCLE_TASK_STATUS': await api.tasks.cycleStatus(action.taskId); break;
    // Handoffs
    case 'ADD_HANDOFF': await api.handoffs.create(action.handoff); break;
    case 'ARCHIVE_HANDOFF': await api.handoffs.update(action.handoffId, { archived: true }); break;
    // Tickets
    case 'CREATE_TICKET': await api.tickets.create(action.ticket as any); break;
    case 'UPDATE_TICKET': await api.tickets.update(action.ticket.id, action.ticket as any); break;
    case 'SET_TICKET_STATUS': await api.tickets.update(action.ticketId, { status: action.status } as any); break;
    case 'ASSIGN_TICKET': await api.tickets.update(action.ticketId, { assigneeId: action.assigneeId } as any); break;
    // Escalations
    case 'CREATE_ESCALATION': await api.escalations.create(action.escalation as any); break;
    case 'ACKNOWLEDGE_ESCALATION':
      await api.escalations.update(action.id, { status: 'acknowledged', acknowledgedBy: action.userId } as any); break;
    case 'RESOLVE_ESCALATION':
      await api.escalations.update(action.id, { status: 'resolved', resolvedBy: action.userId } as any); break;
    case 'ASSIGN_ESCALATION':
      await api.escalations.update(action.id, { assigneeId: action.userId } as any); break;
    case 'BUMP_SEVERITY':
      await api.escalations.bumpSeverity(action.id); break;
    // Incident Response
    case 'CREATE_IR_CASE':
      await api.incidentResponses.create(action.irCase as any); break;
    case 'UPDATE_IR_CASE':
      await api.incidentResponses.update(action.irCase.id, action.irCase as any); break;
    case 'DELETE_IR_CASE':
      await api.incidentResponses.remove(action.id); break;
    case 'CLOSE_IR_CASE':
      await api.incidentResponses.close(action.id); break;
    case 'ADD_IR_NOTE':
      await api.incidentResponses.addNote(action.caseId, action.note.content, action.note.agentTurn); break;
    case 'ADD_IR_IOC':
      await api.incidentResponses.addIoc(action.caseId, {
        type: action.ioc.type, value: action.ioc.value,
        description: action.ioc.description, observedAt: action.ioc.observedAt,
      }); break;
    case 'REMOVE_IR_IOC':
      await api.incidentResponses.removeIoc(action.caseId, action.iocId); break;
    case 'ADD_IR_TIMELINE_EVENT':
      await api.incidentResponses.addTimelineEvent(action.caseId, {
        type: action.event.type, description: action.event.description, occurredAt: action.event.occurredAt,
      }); break;
    // Roster
    case 'UPDATE_ROSTER_SLOT':
      await api.roster.update(action.slotId, { userId: action.userId } as any); break;
    case 'TOGGLE_SLOT_INACTIVE':
      // inactive flag toggled — patch with empty body to trigger server read
      await api.roster.update(action.slotId, {} as any); break;
    // Notifications
    case 'READ_NOTIFICATION': await api.notifications.markRead(action.notifId); break;
    case 'READ_ALL_NOTIFICATIONS': await api.notifications.markAllRead(); break;
    // User profile
    case 'SET_USER_SHIFT': await api.me.updateProfile({ shift: action.shift }); break;
    // Ticket comments
    case 'ADD_TICKET_COMMENT':
      await api.tickets.addComment(action.ticketId, action.content, action.isInternal); break;
    // Task comments
    case 'ADD_TASK_COMMENT': await api.comments.create(action.taskId, action.comment); break;
    // Training
    case 'COMPLETE_MODULE':
      await api.training.completeModule({
        courseId: action.courseId, moduleId: action.moduleId,
        score: action.score, timeSpentMinutes: 25, scormData: {},
      }); break;
    // Ticket mutations
    case 'ESCALATE_TICKET': {
      const et = await api.tickets.get(action.ticketId);
      const now = new Date().toISOString();
      const evt = { id: `tl-${Date.now()}`, type: 'escalation', authorId: currentUserId, content: 'Ticket escalated', oldValue: et.status, newValue: 'escalated', createdAt: now };
      await api.tickets.update(action.ticketId, { status: 'escalated', escalationCount: (et.escalationCount || 0) + 1, timeline: [...et.timeline, evt] } as any);
      break;
    }
    case 'MERGE_TICKET': {
      const now = new Date().toISOString();
      await api.tickets.update(action.sourceId, { status: 'closed', mergedIntoId: action.targetId, closedAt: now } as any);
      const mt = await api.tickets.get(action.targetId);
      await api.tickets.update(action.targetId, { relatedTicketIds: [...mt.relatedTicketIds, action.sourceId] } as any);
      break;
    }
    case 'ADD_WATCHER': {
      const wt = await api.tickets.get(action.ticketId);
      if (!wt.watcherIds.includes(action.userId)) {
        await api.tickets.update(action.ticketId, { watcherIds: [...wt.watcherIds, action.userId] } as any);
      }
      break;
    }
    case 'REMOVE_WATCHER': {
      const rwt = await api.tickets.get(action.ticketId);
      await api.tickets.update(action.ticketId, { watcherIds: rwt.watcherIds.filter((id: string) => id !== action.userId) } as any);
      break;
    }
    case 'LOG_TIME': {
      const lt = await api.tickets.get(action.ticketId);
      const entry = { id: `te-${Date.now()}`, userId: currentUserId, minutes: action.minutes, billable: action.billable, note: action.note, createdAt: new Date().toISOString() };
      await api.tickets.update(action.ticketId, { timeEntries: [...lt.timeEntries, entry], totalTimeMinutes: lt.totalTimeMinutes + action.minutes } as any);
      break;
    }
    case 'APPROVE_TICKET': {
      const at = await api.tickets.get(action.ticketId);
      const approveNow = new Date().toISOString();
      const approvals = at.approvals.map((a: any) => a.approverId === action.approverId ? { ...a, status: action.status, comment: action.comment, decidedAt: approveNow } : a);
      const allApproved = approvals.every((a: any) => a.status === 'approved');
      const anyRejected = approvals.some((a: any) => a.status === 'rejected');
      await api.tickets.update(action.ticketId, { approvals, approvalStatus: anyRejected ? 'rejected' : allApproved ? 'approved' : 'pending' } as any);
      break;
    }
    case 'ADD_TIMELINE_EVENT': {
      const tlt = await api.tickets.get(action.ticketId);
      await api.tickets.update(action.ticketId, { timeline: [...tlt.timeline, action.event] } as any);
      break;
    }
    case 'RATE_TICKET':
      await api.tickets.update(action.ticketId, { csatScore: action.score, csatComment: action.comment } as any); break;
    // Article comments
    case 'ADD_ARTICLE_COMMENT': await api.articles.addComment(action.articleId, action.content); break;
    case 'DELETE_ARTICLE_COMMENT': await api.articles.deleteComment(action.articleId, action.commentId); break;
    // RBAC: handled directly by AdminPage (API-first) — see UI_ONLY_ACTIONS
    // Retention
    case 'UPDATE_RETENTION_POLICY': await api.retention.update(action.policy.id, action.policy); break;
    // Notification prefs
    case 'UPDATE_NOTIFICATION_PREFS': await api.me.updatePrefs(action.prefs); break;
    // Handoffs
    case 'UPDATE_HANDOFF': await api.handoffs.update(action.handoff.id, action.handoff); break;
    // Project/Task CRUD
    case 'CREATE_TASK':
      // Standalone tasks (no project) post to /api/tasks; project-scoped tasks
      // keep the existing /api/projects/:id/tasks route.
      if (action.projectId) {
        return api.tasks.create(action.projectId, action.task);
      } else {
        return api.tasks.createStandalone(action.task);
      }
    case 'DELETE_TASK': await api.tasks.remove(action.taskId); break;
    case 'DELETE_PROJECT': await api.projects.remove(action.projectId); break;
    // Enhanced project & task actions
    case 'ARCHIVE_PROJECT': await api.projects.update(action.projectId, { archived: true } as any); break;
    case 'RESTORE_PROJECT': await api.projects.update(action.projectId, { archived: false } as any); break;
    // Kanban — moving a project between columns just patches the project's
    // `column` string. No separate column container to coordinate with.
    case 'MOVE_PROJECT_TO_COLUMN':
      await api.projects.update(action.projectId, { column: action.column } as any);
      break;
    case 'LOG_TASK_TIME':
      await api.taskTime.log(action.taskId, {
        minutes: action.minutes, billable: action.billable, note: action.note, workDate: action.workDate,
      });
      break;
    case 'DELETE_TASK_TIME':
      await api.taskTime.remove(action.taskId, action.entryId); break;
    case 'ADD_TASK_CHECKLIST':
      await api.taskChecklist.add(action.taskId, action.text); break;
    case 'TOGGLE_TASK_CHECKLIST':
      await api.taskChecklist.toggle(action.taskId, action.itemId, {}); break;
    case 'REMOVE_TASK_CHECKLIST':
      await api.taskChecklist.remove(action.taskId, action.itemId); break;
    case 'SET_TASK_DEPENDENCIES':
      await api.taskAdv.setDependencies(action.taskId, action.blockedByIds); break;
    case 'TOGGLE_TASK_WATCHER': {
      // Optimistically toggle on the server — if already a watcher, remove; else add.
      // The reducer above mirrors this logic locally.
      const tQ = await api.tasks.update(action.taskId, {} as any).catch(() => null);
      void tQ;
      // Use add+catch then remove — simpler: ask server idempotently
      try {
        await api.taskAdv.watch(action.taskId, action.userId);
      } catch { /* may already be watcher */ }
      break;
    }
    case 'BULK_UPDATE_TASKS':
      await api.taskAdv.bulk(action.taskIds, undefined, action.updates); break;
    case 'BULK_ARCHIVE_TASKS':
      await api.taskAdv.bulk(action.taskIds, 'archive'); break;
    case 'ADD_PROJECT_STATUS_UPDATE':
      return api.projectAdv.addStatusUpdate(action.projectId, action.update);
    case 'DELETE_PROJECT_STATUS_UPDATE':
      await api.projectAdv.deleteStatusUpdate(action.projectId, action.updateId); break;
    case 'CREATE_SUBTASK':
      if (action.projectId) {
        return api.tasks.create(action.projectId, action.task);
      } else {
        return api.tasks.createStandalone(action.task);
      }
    case 'CREATE_SPRINT':
      await api.sprints.create(action.projectId, action.sprint); break;
    case 'UPDATE_SPRINT':
      await api.sprints.update(action.sprintId, action.updates); break;
    case 'DELETE_SPRINT':
      await api.sprints.remove(action.sprintId); break;
    case 'CREATE_GOAL':
      await api.goals.create(action.goal); break;
    case 'UPDATE_GOAL':
      await api.goals.update(action.goalId, action.updates); break;
    case 'DELETE_GOAL':
      await api.goals.remove(action.goalId); break;
    case 'CREATE_TEMPLATE':
      await api.projectTemplates.create(action.template); break;
    case 'DELETE_TEMPLATE':
      await api.projectTemplates.remove(action.templateId); break;
    case 'CREATE_PROJECT_VIEW':
      await api.projectViews.create(action.view); break;
    case 'UPDATE_PROJECT_VIEW':
      await api.projectViews.update(action.viewId, action.updates); break;
    case 'DELETE_PROJECT_VIEW':
      await api.projectViews.remove(action.viewId); break;
    // Categories
    case 'CREATE_CATEGORY': await api.categories.create(action.category); break;
    case 'UPDATE_CATEGORY': await api.categories.update(action.category.id, action.category); break;
    case 'DELETE_CATEGORY': await api.categories.remove(action.categoryId); break;
    // MOVE_ARTICLES: API call handled by handleBulkMove() in KBPage — no persistAction needed
    // Decision Trees
    case 'ADD_DECISION_TREE': await api.decisionTrees.create(action.tree); break;
    case 'UPDATE_DECISION_TREE': await api.decisionTrees.update(action.tree.id, action.tree); break;
    case 'DELETE_DECISION_TREE': await api.decisionTrees.remove(action.treeId); break;
    // User management
    case 'UPDATE_REGISTERED_USER':
      await api.users.update(action.userId, action.updates); break;
    case 'REMOVE_REGISTERED_USER':
      await api.users.remove(action.userId); break;
    default:
      // No API call needed for navigation, UI, theme, etc.
      break;
  }
}

const CONFIRMED_PROJECT_ACTIONS = new Set(['CREATE_PROJECT', 'CREATE_TASK', 'CREATE_SUBTASK', 'ADD_PROJECT_STATUS_UPDATE']);
let projectsSaveRevision = 0;

function teamsNotificationWarning(result: any): AppToast | null {
  const undeliverable = result?._teamsNotify?.undeliverable;
  if (!Array.isArray(undeliverable) || undeliverable.length === 0) return null;
  const names = undeliverable.map((u: { name?: string; userId: string }) => u.name || u.userId).join(', ');
  return {
    type: 'info', code: 'TEAMS_BOT_NOT_ACTIVATED',
    message: `Assigned. Teams DM not delivered to ${names} - they need to message the MXDR365 bot once in Teams to receive notifications.`,
  };
}

function confirmedAction(action: AppAction, result: any): AppAction {
  if (!CONFIRMED_PROJECT_ACTIONS.has(action.type)) return action;
  if (!result || typeof result.id !== 'string' || !result.id) throw new Error('Save response could not be confirmed. Reload before retrying.');
  if (action.type === 'CREATE_PROJECT') {
    return { ...action, project: { ..._normalizeProject(result), tasks: (result.tasks ?? []).map(_normalizeTask) } };
  }
  if (action.type === 'CREATE_TASK' || action.type === 'CREATE_SUBTASK') {
    if ((result.projectId ?? null) !== (action.projectId ?? null)) throw new Error('Saved task project did not match. Reload before retrying.');
    return { ...action, task: _normalizeTask(result) };
  }
  if (action.type === 'ADD_PROJECT_STATUS_UPDATE') {
    if (result.id !== action.projectId || !Array.isArray(result.statusUpdates)) throw new Error('Status update could not be confirmed. Reload before retrying.');
    const { tasks: _tasks, ...project } = result;
    return { type: 'UPDATE_PROJECT', project: _normalizeProject(project) };
  }
  return action;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, rawDispatch] = useReducer(appReducer, undefined, createInitialState);
  const dataLoadedRef = useRef(false);
  const bcRef = useRef<BroadcastChannel | null>(null);
  const userIdRef = useRef(state.user.id);
  userIdRef.current = state.user.id;

  // ─── Wrapped dispatch: optimistic local update + background API persist ───
  const wrappedDispatch = useCallback((action: AppAction) => {
    const confirmed = CONFIRMED_PROJECT_ACTIONS.has(action.type);
    if (!confirmed) rawDispatch(action);

    // Skip persistence for UI-only actions
    if (UI_ONLY_ACTIONS.has(action.type)) {
      // Still broadcast handoff changes for cross-tab sync
      if (bcRef.current) {
        if (action.type === 'ADD_HANDOFF') bcRef.current.postMessage({ type: 'HANDOFF_CREATED', handoff: (action as any).handoff });
        else if (action.type === 'UPDATE_HANDOFF') bcRef.current.postMessage({ type: 'HANDOFF_UPDATED', handoff: (action as any).handoff });
      }
      return;
    }

    // Persist with retry (up to 2 retries with backoff)
    const attempt = async (retries: number): Promise<void> => {
      try {
        const result: any = await persistAction(action, userIdRef.current);
        if (confirmed) {
          rawDispatch(confirmedAction(action, result));
          projectsSaveRevision++;
        }
        // Surface Teams-notify deliverability warnings: if any newly assigned
        // user hasn't installed/activated the MXDR365 bot, the proactive DM
        // silently skips. Server returns that list in `_teamsNotify` so we
        // can warn the assigner here instead of leaving them guessing.
        const notificationWarning = teamsNotificationWarning(result);
        if (notificationWarning) rawDispatch({ type: 'TOAST', message: notificationWarning });
      } catch (err: any) {
        if (retries > 0) {
          await new Promise(r => setTimeout(r, (3 - retries) * 1500));
          return attempt(retries - 1);
        }
        // All retries exhausted — notify the user so they know the save failed
        console.error(`[auto-save] Failed to persist ${action.type}:`, err);
        rawDispatch({
          type: 'TOAST',
          message: { type: 'error', message: `Save failed: ${err?.message || 'network error'}. Your change may not persist.` } as AppToast,
        });
        setTimeout(() => rawDispatch({ type: 'TOAST', message: null }), 6000);
      }
    };

    // A lost POST response must not silently create a duplicate.
    attempt(confirmed ? 0 : 2);

    // Broadcast handoff changes to other open tabs
    if (bcRef.current) {
      if (action.type === 'ADD_HANDOFF') {
        bcRef.current.postMessage({ type: 'HANDOFF_CREATED', handoff: (action as any).handoff });
      } else if (action.type === 'UPDATE_HANDOFF') {
        bcRef.current.postMessage({ type: 'HANDOFF_UPDATED', handoff: (action as any).handoff });
      }
    }
  }, []);

  const isAdmin = state.user.role === 'admin';
  const isLead = state.user.role === 'lead';
  const canEdit = isPrivilegedRole(state.user.role);
  const isImpersonating = state.impersonatingFrom !== null;

  // ─── RBAC permission checks ────────────────────────────────────────────────
  // Single source of truth: the server-resolved rbacPermissions array that
  // /api/me attaches to the signed-in user's profile. The backend does the
  // assignment→role→permission join, so no client-side matching is needed
  // (the raw roles/assignments lists are only lazy-loaded where they are
  // displayed — see useEnsureRbac).

  // canEditKb: admin/lead, or a 'knowledge-base' canEdit grant that applies
  // globally or to this specific kbId.
  const canEditKb = useCallback((kbId: string): boolean => {
    if (!isKnownUserRole(state.user.role)) return false;
    if (canEdit) return true;
    if (!Array.isArray(state.user.rbacPermissions)) return false;
    for (const perm of state.user.rbacPermissions) {
      if (hasLiteralPermission(perm, 'knowledge-base', 'canEdit')) {
        if (!Object.prototype.hasOwnProperty.call(perm, 'knowledgeBaseIds')) return true;
        if (!Array.isArray(perm.knowledgeBaseIds)
          || !perm.knowledgeBaseIds.every(id => typeof id === 'string' && id.trim().length > 0)) continue;
        if (perm.knowledgeBaseIds.length === 0 || perm.knowledgeBaseIds.includes(kbId)) return true;
      }
    }
    return false;
  }, [canEdit, state.user.role, state.user.rbacPermissions]);

  // Generic view permission check — works for any viewId in the RBAC matrix.
  // viewId is aliased via rbacViewKey() so views grouped under an umbrella
  // permission (e.g. workload → pm-advanced) consult the umbrella's row.
  const _checkViewPerm = useCallback((viewId: string, field: 'canView' | 'canEdit' | 'canDelete'): boolean => {
    if (!isKnownUserRole(state.user.role)) return false;
    // Built-in admin/lead reach is unchanged; custom rows grant only canonical keys.
    if (canEdit) return isKnownNavigationView(viewId);
    if (!isKnownPermissionView(viewId)) return false;
    const key = rbacViewKey(viewId);
    return Array.isArray(state.user.rbacPermissions)
      && state.user.rbacPermissions.some(perm => hasLiteralPermission(perm, key, field));
  }, [canEdit, state.user.role, state.user.rbacPermissions]);

  const canEditView = useCallback((viewId: string) => _checkViewPerm(viewId, 'canEdit'), [_checkViewPerm]);
  const canDeleteView = useCallback((viewId: string) => _checkViewPerm(viewId, 'canDelete'), [_checkViewPerm]);
  // canViewView — true if any custom RBAC role grants explicit canView on this
  // viewId. Used to augment base-role feature-visibility (additive, never reduces).
  const canViewView = useCallback((viewId: string) => _checkViewPerm(viewId, 'canView'), [_checkViewPerm]);

  const navigate = useCallback((view: ViewId, entityId?: string) => {
    if (!isKnownUserRole(state.user.role) || !isKnownView(view)) return;
    // Allow if base role permits OR if custom RBAC grants explicit canView
    // OR the user is GM-tier and the view is in the GENERAL MANAGEMENT
    // section (mirrors the sidebar's GM-tier grant).
    if (!isViewAllowed(state.user.role, view) && !canViewView(view) && !(state.user.tier === 'GM' && isGmView(view))) return;
    wrappedDispatch({ type: 'SET_VIEW', view, entityId });
  }, [wrappedDispatch, state.user.role, state.user.tier, canViewView]);

  // ─── Re-validate the RESTORED view once the real identity arrives ──────────
  //
  // getPersistedView() reads localStorage['mxdr365_view'] and checks it only
  // against VALID_VIEWS. It cannot check permissions: initial state is built
  // before /api/me has resolved, so there is no role to check against. The
  // consequence was that any view ever persisted in a browser came back on the
  // next load regardless of what that user is allowed to see now - a role
  // change, a shared machine, or someone else's session was enough to land an
  // analyst on the Engineering blade. navigate() has always gated correctly;
  // the initial view simply never went through it.
  //
  // BLANK_USER carries id '' and role 'analyst' until SET_CURRENT_USER lands, so
  // this waits on a real id. Without that wait every admin would be bounced
  // home on each load, because for one render they look like an analyst.
  useEffect(() => {
    if (!state.user.id) return;
    if (state.user.role === 'customer') return;  // App.tsx hard-pins the portal
    const view = state.view;
    const permitted = isKnownUserRole(state.user.role) && isKnownView(view) && (isViewAllowed(state.user.role, view)
      || canViewView(view)
      || (state.user.tier === 'GM' && isGmView(view)));
    if (permitted) return;
    console.warn(`[rbac] restored view "${view}" is not permitted for role "${state.user.role}" - returning home`);
    // rawDispatch, not navigate(): navigate() would refuse 'home' only if home
    // were itself blocked, but going through the wrapped dispatch keeps the
    // persisted value in sync via persistView() so the bad entry is overwritten
    // rather than sitting in localStorage waiting for the next load.
    wrappedDispatch({ type: 'SET_VIEW', view: 'home' });
  }, [state.user.id, state.user.role, state.user.tier, state.view, canViewView, wrappedDispatch]);

  const toast = useCallback((msg: string) => {
    rawDispatch({ type: 'TOAST', message: msg });
  }, []);

  const toastError = useCallback((message: string, code?: string) => {
    const t: AppToast = { type: 'error', message, ...(code ? { code } : {}) };
    rawDispatch({ type: 'TOAST', message: t });
  }, []);

  // ─── Confirmed save: call API first, update local state only on success ───
  const saveAction = useCallback(async (action: AppAction) => {
    const result = await persistAction(action, userIdRef.current);
    rawDispatch(confirmedAction(action, result));
    if (CONFIRMED_PROJECT_ACTIONS.has(action.type) || action.type === 'SET_TASK_DEPENDENCIES') projectsSaveRevision++;
    const notificationWarning = teamsNotificationWarning(result);
    if (notificationWarning) rawDispatch({ type: 'TOAST', message: notificationWarning });
    return result;
  }, []);

  // ─── Re-fetch articles from API (call after batch operations) ───
  // Uses the lightweight summaries endpoint — full body/comments/versions are
  // pulled on demand by ArticlePage/EditorPage when a specific article opens.
  const refreshArticles = useCallback(async () => {
    try {
      const articles = await api.articles.listSummaries({ status: 'all' });
      rawDispatch({ type: 'SET_ARTICLE_SUMMARIES', articles: articles as Article[] });
    } catch (err: any) {
      console.error('[refreshArticles] failed:', err);
    }
  }, []);

  // ─── Load all data from API on startup ───
  useEffect(() => {
    if (dataLoadedRef.current) return;
    dataLoadedRef.current = true;

    const loadAll = async () => {
      // Boot fetches ONLY what Sidebar + Home need for first paint:
      //   kbs + categories (KB navigation),
      //   notifications (sidebar badge),
      //   users.directory (display-name resolution everywhere).
      //
      // Articles moved out — useEnsureArticles is called by Sidebar (always
      // mounted) so summaries arrive shortly after first paint without
      // blocking the boot Promise.allSettled. The ETag + stale-while-
      // revalidate on /api/articles-summaries makes repeat visits ~free.
      //
      // RBAC roles + assignments moved out too — permission enforcement runs
      // off the server-resolved user.rbacPermissions from /api/me, so the raw
      // lists are only needed where they are displayed (useEnsureRbac).
      //
      // Everything else — projects, tasks, tickets, escalations, roster,
      // handoffs, IR cases, admin users — is deferred to the routes that
      // consume it via the useEnsure* hooks exported below.
      const [notificationsResult, kbsResult, categoriesResult,
             usersResult] = await Promise.allSettled([
        api.notifications.list(),
        api.kbs.list(),
        api.categories.list(),
        // Slim directory — {id, displayName, email} — for every authed user.
        api.users.directory(),
      ]);

      if (notificationsResult.status === 'fulfilled') {
        rawDispatch({ type: 'SET_NOTIFICATIONS', notifications: notificationsResult.value as Notification[] });
      }

      if (kbsResult.status === 'fulfilled') {
        const kbs = kbsResult.value as KnowledgeBase[];
        rawDispatch({ type: 'SET_KNOWLEDGE_BASES', kbs });
        // Resolve activeKnowledgeBaseId to a concrete KB id. The literal
        // 'default' sentinel from initial state / older localStorage values
        // breaks the article filter in KBPage when the real default KB has a
        // generated id like 'kb_1744…' — `artKbId === activeKbId` never
        // matches the new-id articles, so the folder appears empty. Always
        // swap the sentinel for the real default KB's id. '__all__' is a
        // valid cross-KB sentinel and is preserved as-is.
        const persisted = getPersistedActiveKb();
        const isRealKb = kbs.some(kb => kb.id === persisted);
        if (!isRealKb && persisted !== '__all__') {
          const defaultKb = kbs.find(kb => kb.isDefault) || kbs[0];
          if (defaultKb) {
            rawDispatch({ type: 'SET_ACTIVE_KB', kbId: defaultKb.id });
          }
        }
      } else {
        console.error('[loadAll] kbs failed:', kbsResult.reason);
      }

      if (categoriesResult.status === 'fulfilled') {
        // Merge API categories with any locally-persisted ones that the API
        // doesn't know about yet (e.g. created while offline or if create failed).
        // API is authoritative — its version wins for any id that exists in both.
        const apiCats = categoriesResult.value as Category[];
        const apiIds = new Set(apiCats.map(c => c.id));
        const localOnly = getPersistedCategories().filter(c => !apiIds.has(c.id));
        rawDispatch({ type: 'SET_CATEGORIES', categories: [...apiCats, ...localOnly] });
      } else {
        console.error('[loadAll] categories failed:', categoriesResult.reason);
      }

      if (usersResult.status === 'fulfilled') {
        const dir = usersResult.value as import('../services/api').UserDirectoryEntry[];
        // Baseline from slim directory — name/email/avatar for every authed user.
        // AdminPage calls useEnsureAdminUsers() to overlay the richer
        // role/tier/shift fields once it mounts.
        const users: User[] = dir.map(p => ({
          id: p.id,
          name: p.displayName,
          shift: 'Shift 1',
          tier: 'T1',
          avatar: p.avatarUrl || '👤',
          role: 'analyst',
          email: p.email,
          entraOid: p.id,
        }));
        rawDispatch({ type: 'SET_REGISTERED_USERS', users });
        // Keep the static USERS lookup in sync so `getUserName(id)` resolves
        // real Entra-backed users instead of returning "Unknown" everywhere.
        USERS.length = 0;
        USERS.push(...users);
      }
    };

    loadAll().catch(console.error);
  }, []);

  // The public dispatch exposed via context — wraps rawDispatch with API persistence
  const dispatch = wrappedDispatch;

  const getUser = useCallback((id: string) =>
    state.registeredUsers.find(u => u.id === id) || USERS.find(u => u.id === id),
    [state.registeredUsers],
  );

  const getAllTasks = useCallback(
    () => [...state.projects.flatMap(p => p.tasks), ...state.standaloneTasks],
    [state.projects, state.standaloneTasks],
  );

  const getMyTasks = useCallback(() =>
    [...state.projects.flatMap(p => p.tasks), ...state.standaloneTasks].filter(t => isAssignedTo(t, state.user.id)),
    [state.projects, state.standaloneTasks, state.user.id],
  );

  // Presence broadcasting via BroadcastChannel with heartbeat
  useEffect(() => {
    const channelName = 'mxdr365_presence';
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel(channelName);
      bcRef.current = bc;
    } catch { /* not supported */ }

    // Broadcast own presence (called on mount, view change, and every heartbeat)
    const broadcastPresence = () => {
      const myPresence: PresenceUser = {
        userId: state.user.id,
        userName: state.user.name,
        view: state.view,
        lastSeen: Date.now(),
      };
      bc?.postMessage(myPresence);
      rawDispatch({ type: 'UPDATE_PRESENCE', user: myPresence });
    };

    // Broadcast immediately
    broadcastPresence();

    // Heartbeat: re-broadcast every 15s so lastSeen stays fresh
    const heartbeat = setInterval(broadcastPresence, 15_000);

    // Prune stale users every 20s (anyone not seen in 90s is gone).
    // This also triggers re-renders so the "online" filter re-evaluates.
    const pruneInterval = setInterval(() => {
      rawDispatch({ type: 'PRUNE_PRESENCE', cutoff: Date.now() - 90_000 });
    }, 20_000);

    if (bc) {
      bc.onmessage = (e: MessageEvent<any>) => {
        const data = e.data;
        // Presence message (legacy shape: has userId + view)
        if (data && data.userId && data.view !== undefined) {
          rawDispatch({ type: 'UPDATE_PRESENCE', user: data as PresenceUser });
          return;
        }
        // Handoff sync messages
        if (data?.type === 'HANDOFF_CREATED') {
          rawDispatch({ type: 'ADD_HANDOFF', handoff: data.handoff });
        } else if (data?.type === 'HANDOFF_UPDATED') {
          rawDispatch({ type: 'UPDATE_HANDOFF', handoff: data.handoff });
        }
      };
    }

    // Auto-refresh theme for auto mode
    const themeInterval = setInterval(() => {
      if (state.themeSchedule.mode === 'auto') {
        const resolved = getAutoTheme(state.themeSchedule.lightStartTime, state.themeSchedule.lightEndTime);
        if (resolved !== state.activeThemeMode) {
          rawDispatch({ type: 'SET_ACTIVE_THEME', mode: resolved });
        }
      }
    }, 60000);

    return () => {
      bc?.close();
      bcRef.current = null;
      clearInterval(heartbeat);
      clearInterval(pruneInterval);
      clearInterval(themeInterval);
    };
  }, [state.user.id, state.user.name, state.view, state.themeSchedule, state.activeThemeMode, wrappedDispatch]);

  // ─── Server-side presence heartbeat ──────────────────────────────────────
  // Posts to /api/presence/heartbeat every 30s (so other devices see this
  // user as online) and refreshes the global online-users list every 30s.
  // Disabled until we have a real signed-in userId so guest/blank renders
  // don't hammer the API.
  useEffect(() => {
    if (!state.user.id || state.user.id === BLANK_USER.id) return;

    let cancelled = false;
    const sendHeartbeat = async () => {
      try {
        await api.presence.heartbeat(state.view);
      } catch { /* offline / 401 — keep trying on next tick */ }
    };
    const refreshOnline = async () => {
      try {
        const res = await api.presence.online(120);
        if (cancelled) return;
        const users: PresenceUser[] = res.users.map(u => ({
          userId: u.userId,
          userName: u.userName,
          email: u.email,
          view: u.view,
          lastSeen: u.lastSeenMs,
        }));
        rawDispatch({ type: 'SET_ONLINE_USERS', users });
      } catch { /* non-fatal */ }
    };

    sendHeartbeat();
    refreshOnline();
    const hbInterval = setInterval(sendHeartbeat, 30_000);
    const refreshInterval = setInterval(refreshOnline, 30_000);

    return () => {
      cancelled = true;
      clearInterval(hbInterval);
      clearInterval(refreshInterval);
    };
  }, [state.user.id, state.view]);

  // ─── Sync navigation state to URL hash (survives page refresh) ───
  useEffect(() => {
    // MSAL must consume its callback before navigation replaces the fragment.
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    if (['code', 'state', 'id_token', 'access_token', 'error'].some(key => fragment.has(key))) return;

    const newHash = buildHash(
      state.view, state.adminTab, state.freshServiceTab, state.lmsTab, state.pmMode,
      state.selectedArticleId, state.selectedProjectId, state.selectedTaskId, state.selectedTicketId,
      state.selectedCustomerClient, state.expandedFsTicketId, state.selectedDecisionTree,
      state.expandedProjectTask, state.expandedAuditEntry,
      state.selectedCategoryId, state.activeKnowledgeBaseId,
      state.selectedIRCaseId, state.calendarDrillProjectId,
      state.selectedProvisioningRequestId,
    );
    // Pin the URL to the app root, not just the hash. SCORM course content
    // runs same-origin, and a misbehaving package can navigate or
    // replaceState the top window to a package-relative path (e.g.
    // /scorm_package/shared/index.html). The SWA navigationFallback serves
    // the SPA from any path, so the app keeps working — and a hash-only
    // replaceState would then carry that junk path forever, including into
    // copied share links. Rewriting the full path self-heals it.
    const basePath = import.meta.env.BASE_URL || '/';
    if (window.location.pathname !== basePath || window.location.hash !== newHash) {
      window.history.replaceState(null, '', basePath + window.location.search + newHash);
    }
  }, [state.view, state.adminTab, state.freshServiceTab, state.lmsTab, state.pmMode,
      state.selectedArticleId, state.selectedProjectId, state.selectedTaskId, state.selectedTicketId,
      state.selectedCustomerClient, state.expandedFsTicketId, state.selectedDecisionTree,
      state.expandedProjectTask, state.expandedAuditEntry,
      state.selectedCategoryId, state.activeKnowledgeBaseId,
      state.selectedIRCaseId, state.calendarDrillProjectId,
      state.selectedProvisioningRequestId]);

  // Swap the shared mutable `theme` object (imported statically all over the
  // app) BEFORE children render, so every component reads the active palette.
  applyThemeMode(state.activeThemeMode);
  const currentTheme = getTheme(state.activeThemeMode);

  return (
    <AppContext.Provider value={{ state, dispatch: wrappedDispatch, isAdmin, isLead, canEdit, isImpersonating, navigate, toast, toastError, getUser, getAllTasks, getMyTasks, saveAction, refreshArticles, canEditKb, canEditView, canDeleteView, canViewView, currentTheme }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}

// ─── Re-export static data for components ───
export { USERS, COURSES, CUSTOMERS, GLOSSARY, DECISION_TREES } from '../lib/mockData';
export { initActivity, CLIENT_HEALTH_SCORES, ANALYST_WORKLOADS, SHIFT_ZONES, CHECKLIST_ITEMS, initShiftMetrics } from '../lib/mockData';

/** Merged view: built-in decision trees + DB-stored trees (published for non-admin, all for admin). */
export function useDecisionTrees() {
  const { state } = useApp();
  const builtIn = DECISION_TREES.map(t => ({ ...t, source: 'built-in' as const, status: 'published' as const }));
  const dbTrees = state.dbDecisionTrees;
  // DB trees take precedence if they have the same id as a built-in
  const builtInIds = new Set(dbTrees.map(t => t.id));
  const merged = [...builtIn.filter(t => !builtInIds.has(t.id)), ...dbTrees];
  return merged;
}

// ─── useEnsureArticles ────────────────────────────────────────────────────────
let articlesPromise: Promise<void> | null = null;
let articlesLoaded = false;

/**
 * Lazily fetch the cross-KB article summaries on first mount of any
 * KB-consuming page (Sidebar mounts on every authed view, so this fires
 * once per session right after first paint).
 *
 * Moved out of loadAll() so the boot fan-out doesn't block on articles.
 * The /api/articles-summaries response carries an ETag + stale-while-
 * revalidate, so on repeat visits the browser serves the cached body and
 * the network round-trip is a 304 (or skipped entirely while stale).
 */
export function useEnsureArticles() {
  const { dispatch } = useApp();
  useEffect(() => {
    if (articlesLoaded) return;
    if (!articlesPromise) {
      articlesPromise = api.articles.listSummaries({ status: 'all' })
        .then(articles => {
          dispatch({ type: 'SET_ARTICLE_SUMMARIES', articles: articles as Article[] });
          // Cache for cold-start instant paint (KBPage reads this).
          try { localStorage.setItem('mxdr365_article_summaries', JSON.stringify(articles)); }
          catch { /* quota — ignore */ }
          articlesLoaded = true;
        })
        .catch(err => {
          console.error('[useEnsureArticles] failed:', err);
          articlesPromise = null;
        });
    }
  }, [dispatch]);
}

// Module-level dedupe so multiple pages calling useEnsureTrainingProgress
// produce a single network round-trip. Data is considered fresh for a short
// window and refetched after — completions land on people's machines without
// a full page reload (an analyst finishing a course shows up for the admin
// on their next visit to a training-consuming page).
let trainingProgressPromise: Promise<void> | null = null;
let trainingProgressFetchedAt = 0;
const TRAINING_STALE_MS = 60_000;

async function fetchTrainingData(dispatch: (a: AppAction) => void): Promise<void> {
  const [progressResult, coursesResult, assignmentsResult, groupsResult, ruleResult] = await Promise.allSettled([
    api.training.allProgress(),
    api.training.courses(),
    api.training.assignments(),
    api.training.groups(),
    api.training.remedialRule(),
  ]);
  if (progressResult.status === 'fulfilled') {
    const grouped: Record<string, TrainingProgress[]> = {};
    for (const p of progressResult.value as TrainingProgress[]) { (grouped[p.userId] ??= []).push(p); }
    dispatch({ type: 'SET_TRAINING_PROGRESS', progress: grouped });
  } else {
    console.error('[fetchTrainingData] progress failed:', progressResult.reason);
  }
  if (coursesResult.status === 'fulfilled') {
    const courses = coursesResult.value as Course[];
    dispatch({ type: 'SET_COURSES', courses });
    // Keep the static COURSES lookup in sync (same pattern as USERS)
    // so pages that still read the module-level array see real data.
    COURSES.length = 0;
    COURSES.push(...courses);
  } else {
    console.error('[fetchTrainingData] courses failed:', coursesResult.reason);
  }
  if (assignmentsResult.status === 'fulfilled') {
    dispatch({ type: 'SET_TRAINING_ASSIGNMENTS', assignments: assignmentsResult.value as TrainingAssignment[] });
  } else {
    console.error('[fetchTrainingData] assignments failed:', assignmentsResult.reason);
  }
  if (groupsResult.status === 'fulfilled') {
    dispatch({ type: 'SET_TRAINING_GROUPS', groups: groupsResult.value as TrainingGroup[] });
  } else {
    console.error('[fetchTrainingData] groups failed:', groupsResult.reason);
  }
  if (ruleResult.status === 'fulfilled') {
    dispatch({ type: 'SET_REMEDIAL_RULE', rule: ruleResult.value as RemedialRule });
  }
  if (progressResult.status === 'fulfilled' && coursesResult.status === 'fulfilled') {
    trainingProgressFetchedAt = Date.now();
  }
}

/**
 * Lazily fetch global training progress + the SCORM course catalog on first
 * mount of any consuming page, refetching when the cached copy is older than
 * TRAINING_STALE_MS. Removed from app startup to keep the critical-path load
 * fast — pages that need cross-user progress or courses (Admin, Reports,
 * LMS, Home) call this hook.
 */
export function useEnsureTrainingProgress() {
  const { dispatch } = useApp();
  useEffect(() => {
    if (Date.now() - trainingProgressFetchedAt < TRAINING_STALE_MS) return;
    if (!trainingProgressPromise) {
      trainingProgressPromise = fetchTrainingData(dispatch)
        .finally(() => { trainingProgressPromise = null; });
    }
  }, [dispatch]);
}

/** Manual "Refresh" for training views — always refetches, awaitable. */
export function useRefreshTraining() {
  const { dispatch } = useApp();
  return useCallback(async () => {
    if (!trainingProgressPromise) {
      trainingProgressPromise = fetchTrainingData(dispatch)
        .finally(() => { trainingProgressPromise = null; });
    }
    await trainingProgressPromise;
  }, [dispatch]);
}

// ─── Helpers shared by useEnsureProjects ─────────────────────────────────────
function _normalizeTask(t: any) {
  return {
    ...t,
    labels: t.labels ?? [],
    comments: t.comments ?? [],
    checklist: t.checklist ?? [],
    activity: t.activity ?? [],
    timeEntries: t.timeEntries ?? [],
    watcherIds: t.watcherIds ?? [],
    blockedByIds: t.blockedByIds ?? [],
    blocksTaskIds: t.blocksTaskIds ?? [],
    attachments: t.attachments ?? [],
  };
}
// Map legacy container-backed column ids to their default-column display name
// so projects rendered through the old model still land in the right Kanban
// column on first paint. Custom (non-default) `col_*` ids fall through to the
// 'General' fallback — there is no longer a server-side dictionary to look
// them up against. Authoritative read is `project.column` (the string).
const LEGACY_COLUMN_ID_TO_NAME: Record<string, string> = {
  col_engineering: 'Engineering',
  col_threat_intelligence: 'Threat Intelligence',
  col_general: 'General',
};

function _normalizeProject(p: any) {
  // Auto-migrate the legacy `columnId` field to the new `column` (string).
  // Once `column` is set the next PATCH from the UI persists it; reads stay
  // tolerant either way.
  let column: string | undefined = p.column;
  if (!column && p.columnId) {
    column = LEGACY_COLUMN_ID_TO_NAME[p.columnId];
  }
  return {
    ...p,
    column,
    labels: p.labels ?? [],
    statusUpdates: p.statusUpdates ?? [],
    milestones: p.milestones ?? [],
    sprintIds: p.sprintIds ?? [],
    customFieldDefs: p.customFieldDefs ?? [],
    checklist: p.checklist ?? [],
    goalIds: p.goalIds ?? [],
    watcherIds: p.watcherIds ?? [],
    memberIds: p.memberIds ?? [],
  };
}

// ─── useEnsureProjects ────────────────────────────────────────────────────────
let projectsPromise: Promise<void> | null = null;
let projectsLoaded = false;
let projectsDetailRevision = 0;

/** Lazily fetch projects + tasks on first mount of any project-consuming page. */
export function useEnsureProjects(projectId?: string | null) {
  const { dispatch } = useApp();
  const [loading, setLoading] = useState(!projectsLoaded);
  const [error, setError] = useState<string | null>(null);
  const [attempt, retry] = useReducer(n => n + 1, 0);
  useEffect(() => {
    let active = true;
    if (projectId) {
      const loadRevision = projectsSaveRevision;
      // A selected project's newer read must not be replaced by an older board snapshot.
      projectsDetailRevision++;
      setLoading(true);
      setError(null);
      Promise.all([api.projects.get(projectId), api.tasks.list(projectId)])
        .then(([project, tasks]) => {
          if (!active) return;
          if (loadRevision !== projectsSaveRevision) throw new Error('Project changed during loading.');
          if (!project || project.id !== projectId || !Array.isArray(tasks)
            || tasks.some(task => !task || task.projectId !== projectId)) {
            throw new Error('Invalid project detail response.');
          }
          const snapshot = partitionProjectTasks(tasks);
          dispatch({ type: 'SET_PROJECT_DETAIL', project: {
            ..._normalizeProject(project), tasks: snapshot.tasks.map(_normalizeTask),
          }, unsupportedTasks: snapshot.unsupportedTasks });
        })
        .catch(() => {
          if (active) setError('This project or its tasks could not be loaded. Retry to refresh them.');
        })
        .finally(() => { if (active) setLoading(false); });
      return () => { active = false; };
    }
    if (projectsLoaded) { setLoading(false); setError(null); return; }
    setLoading(true);
    setError(null);
    if (!projectsPromise) {
      const loadRevision = projectsSaveRevision;
      const detailRevision = projectsDetailRevision;
      // Projects + tasks load on the critical path. The Kanban column override
      // is fetched separately and never blocks the board from rendering — the
      // client already has hardcoded defaults to fall back to.
      projectsPromise = Promise.all([api.projects.list(), loadProjectTaskPages(api.tasks.board)])
        .then(([projectRows, taskRows]) => {
            // Never replace a confirmed write with an older in-flight snapshot.
            if (loadRevision !== projectsSaveRevision || detailRevision !== projectsDetailRevision) {
              throw new Error('Projects changed during loading. Retry to refresh them.');
            }
            const allTasks = taskRows.tasks.map(_normalizeTask);
            const projects = projectRows.map(p => ({
              ..._normalizeProject(p),
              tasks: allTasks.filter((t: any) => t.projectId === p.id),
            }));
            dispatch({ type: 'SET_PROJECTS', projects });
            const standalone = allTasks.filter((t: any) => !t.projectId);
            dispatch({ type: 'SET_STANDALONE_TASKS', tasks: standalone });
            dispatch({ type: 'SET_UNSUPPORTED_PROJECT_TASKS', tasks: taskRows.unsupportedTasks });
          projectsLoaded = true;
        })
        .finally(() => {
          projectsPromise = null;
        });
      // Fire-and-forget: load the admin column override list. Failures here
      // are non-fatal — defaults render anyway.
      api.projectColumns.list()
        .then(({ columns }) => dispatch({ type: 'SET_PROJECT_COLUMNS_OVERRIDE', columns: columns ?? null }))
        .catch(err => console.warn('[useEnsureProjects] column override fetch failed:', err));
    }
    projectsPromise.then(() => {
      if (active) setLoading(false);
    }).catch(() => {
      if (active) { setLoading(false); setError('Projects or tasks could not be loaded. Retry to refresh them.'); }
    });
    return () => { active = false; };
  }, [dispatch, attempt, projectId]);
  return { loading, error, retry };
}

// ─── useEnsureTickets ─────────────────────────────────────────────────────────
let ticketsPromise: Promise<void> | null = null;
let ticketsLoaded = false;

/** Lazily fetch Cosmos tickets on first mount of any ticket-consuming page. */
export function useEnsureTickets() {
  const { dispatch } = useApp();
  useEffect(() => {
    if (ticketsLoaded) return;
    if (!ticketsPromise) {
      ticketsPromise = api.tickets.list({ limit: 200 })
        .then(tickets => {
          dispatch({ type: 'SET_TICKETS', tickets: tickets as Ticket[] });
          ticketsLoaded = true;
        })
        .catch(err => {
          console.error('[useEnsureTickets] failed:', err);
          ticketsPromise = null;
        });
    }
  }, [dispatch]);
}

// ─── useEnsureEscalations ─────────────────────────────────────────────────────
let escalationsPromise: Promise<void> | null = null;
let escalationsLoaded = false;

/** Lazily fetch escalations on first mount of EscalationPage. */
export function useEnsureEscalations() {
  const { dispatch } = useApp();
  useEffect(() => {
    if (escalationsLoaded) return;
    if (!escalationsPromise) {
      escalationsPromise = api.escalations.list()
        .then(escalations => {
          dispatch({ type: 'SET_ESCALATIONS', escalations: escalations as IncidentEscalation[] });
          escalationsLoaded = true;
        })
        .catch(err => {
          console.error('[useEnsureEscalations] failed:', err);
          escalationsPromise = null;
        });
    }
  }, [dispatch]);
}

// ─── useEnsureRosterSlots ─────────────────────────────────────────────────────
let rosterSlotsPromise: Promise<void> | null = null;
let rosterSlotsLoaded = false;

/** Lazily fetch roster slots on first mount of ShiftRosterPage. */
export function useEnsureRosterSlots() {
  const { dispatch } = useApp();
  useEffect(() => {
    if (rosterSlotsLoaded) return;
    if (!rosterSlotsPromise) {
      rosterSlotsPromise = api.rosterSlots.list()
        .then(rosterSlots => {
          dispatch({ type: 'SET_ROSTER_SLOTS', rosterSlots: rosterSlots as RosterSlot[] });
          rosterSlotsLoaded = true;
        })
        .catch(err => {
          console.error('[useEnsureRosterSlots] failed:', err);
          rosterSlotsPromise = null;
        });
    }
  }, [dispatch]);
}

// ─── useEnsureIRCases ─────────────────────────────────────────────────────────
let irCasesPromise: Promise<void> | null = null;
let irCasesLoaded = false;

/** Lazily fetch IR cases on first mount of IncidentResponsePage. */
export function useEnsureIRCases() {
  const { dispatch } = useApp();
  useEffect(() => {
    if (irCasesLoaded) return;
    if (!irCasesPromise) {
      irCasesPromise = api.incidentResponses.list()
        .then(cases => {
          dispatch({ type: 'SET_IR_CASES', cases: cases as IncidentResponseCase[] });
          irCasesLoaded = true;
        })
        .catch(err => {
          console.error('[useEnsureIRCases] failed:', err);
          irCasesPromise = null;
        });
    }
  }, [dispatch]);
}

// ─── useEnsureRbac ────────────────────────────────────────────────────────────
let rbacPromise: Promise<void> | null = null;
let rbacLoaded = false;

/** Lazily fetch the raw custom-role + assignment lists. Only needed where the
 *  lists themselves are displayed (AdminPage RBAC tab, IR agent pickers) —
 *  permission ENFORCEMENT runs off the server-resolved user.rbacPermissions
 *  from /api/me and never touches these. */
export function useEnsureRbac() {
  const { dispatch } = useApp();
  useEffect(() => {
    if (rbacLoaded) return;
    if (!rbacPromise) {
      rbacPromise = Promise.all([api.rbac.listRoles(), api.rbac.listAssignments()])
        .then(([roles, assignments]) => {
          dispatch({ type: 'SET_CUSTOM_ROLES', roles: roles as CustomRole[] });
          dispatch({ type: 'SET_ROLE_ASSIGNMENTS', assignments: assignments as UserRoleAssignment[] });
          rbacLoaded = true;
        })
        .catch(err => {
          console.error('[useEnsureRbac] failed:', err);
          rbacPromise = null;
        });
    }
  }, [dispatch]);
}

// ─── useEnsureTraction ────────────────────────────────────────────────────────
let tractionPromise: Promise<void> | null = null;
let tractionLoaded = false;

function loadTractionData(dispatch: Dispatch<AppAction>) {
  tractionPromise = Promise.all([
    api.gmTraction.rocks.list(),
    api.gmTraction.scorecard.list(),
    api.gmTraction.issues.list(),
    api.gmTraction.todos.list(),
    api.gmTraction.meetings.list(),
    api.gmTraction.people.list(),
    api.gmTraction.people.getConfig(),
  ])
    .then(([rocks, metrics, issues, todos, meetings, people, config]) => {
      dispatch({
        type: 'SET_TRACTION_DATA',
        data: { rocks, metrics, issues, todos, meetings, people, coreValues: config.coreValues, loaded: true, loadError: undefined },
      });
      tractionLoaded = true;
    })
    .catch(err => {
      console.error('[useEnsureTraction] failed:', err);
      tractionPromise = null;
      // Mark loaded with an error so TractionPage can show a retry affordance
      // instead of an eternal loading state (the effect won't re-run while the
      // page stays mounted).
      dispatch({
        type: 'SET_TRACTION_DATA',
        data: { loaded: true, loadError: err instanceof Error ? err.message : 'Failed to load EOS data' },
      });
    });
}

/**
 * Lazily fetch all EOS/Traction data (rocks, scorecard, issues, to-dos, L10
 * history, People Analyzer + core-values config) on first mount of
 * TractionPage. One boot fan-out per session; mutations keep the slice in
 * sync via TRACTION_UPSERT/REMOVE, so no refetch is needed afterwards.
 */
export function useEnsureTraction() {
  const { dispatch } = useApp();
  useEffect(() => {
    if (tractionLoaded || tractionPromise) return;
    loadTractionData(dispatch);
  }, [dispatch]);
}

/** Re-run the traction boot fan-out after a failed load (see loadError). */
export function retryTraction(dispatch: Dispatch<AppAction>) {
  if (tractionPromise) return;
  dispatch({ type: 'SET_TRACTION_DATA', data: { loaded: false, loadError: undefined } });
  loadTractionData(dispatch);
}

// ─── useEnsureHiring ──────────────────────────────────────────────────────────
let hiringPromise: Promise<void> | null = null;
let hiringLoaded = false;

function loadHiringData(dispatch: Dispatch<AppAction>) {
  hiringPromise = Promise.all([
    api.gmHiring.requisitions.list(),
    api.gmHiring.candidates.list(),
    api.gmHiring.interviews.list(),
  ])
    .then(([requisitions, candidates, interviews]) => {
      dispatch({
        type: 'SET_HIRING_DATA',
        data: { requisitions, candidates, interviews, loaded: true, loadError: undefined },
      });
      hiringLoaded = true;
    })
    .catch(err => {
      console.error('[useEnsureHiring] failed:', err);
      hiringPromise = null;
      // Mark loaded with an error so HiringPage can show a retry affordance
      // instead of an eternal loading state (the effect won't re-run while the
      // page stays mounted).
      dispatch({
        type: 'SET_HIRING_DATA',
        data: { loaded: true, loadError: err instanceof Error ? err.message : 'Failed to load hiring data' },
      });
    });
}

/**
 * Lazily fetch all hiring data (requisitions, candidates, interviews) on
 * first mount of HiringPage. One boot fan-out per session; mutations keep
 * the slice in sync via HIRING_UPSERT/REMOVE, so no refetch is needed.
 */
export function useEnsureHiring() {
  const { dispatch } = useApp();
  useEffect(() => {
    if (hiringLoaded || hiringPromise) return;
    loadHiringData(dispatch);
  }, [dispatch]);
}

/** Re-run the hiring boot fan-out after a failed load (see loadError). */
export function retryHiring(dispatch: Dispatch<AppAction>) {
  if (hiringPromise) return;
  dispatch({ type: 'SET_HIRING_DATA', data: { loaded: false, loadError: undefined } });
  loadHiringData(dispatch);
}

// ─── useEnsurePeople ──────────────────────────────────────────────────────────
let peopleOpsPromise: Promise<void> | null = null;
let peopleOpsLoaded = false;

function loadPeopleData(dispatch: Dispatch<AppAction>) {
  peopleOpsPromise = Promise.all([
    api.gmPeople.reviews.list(),
    api.gmPeople.oneOnOnes.list(),
    api.gmPeople.wellbeing.list(),
    api.gmPeople.attrition.list(),
  ])
    .then(([reviews, oneOnOnes, wellbeing, attrition]) => {
      dispatch({
        type: 'SET_PEOPLE_DATA',
        data: { reviews, oneOnOnes, wellbeing, attrition, loaded: true, loadError: undefined },
      });
      peopleOpsLoaded = true;
    })
    .catch(err => {
      console.error('[useEnsurePeople] failed:', err);
      peopleOpsPromise = null;
      // Mark loaded with an error so PeoplePage can show a retry affordance
      // instead of an eternal loading state (the effect won't re-run while the
      // page stays mounted).
      dispatch({
        type: 'SET_PEOPLE_DATA',
        data: { loaded: true, loadError: err instanceof Error ? err.message : 'Failed to load people data' },
      });
    });
}

/**
 * Lazily fetch all People-Ops data (reviews, 1:1s, wellbeing check-ins,
 * retention records) on first mount of PeoplePage. One boot fan-out per
 * session; mutations keep the slice in sync via PEOPLE_UPSERT/REMOVE, so no
 * refetch is needed.
 */
export function useEnsurePeople() {
  const { dispatch } = useApp();
  useEffect(() => {
    if (peopleOpsLoaded || peopleOpsPromise) return;
    loadPeopleData(dispatch);
  }, [dispatch]);
}

/** Re-run the people-ops boot fan-out after a failed load (see loadError). */
export function retryPeople(dispatch: Dispatch<AppAction>) {
  if (peopleOpsPromise) return;
  dispatch({ type: 'SET_PEOPLE_DATA', data: { loaded: false, loadError: undefined } });
  loadPeopleData(dispatch);
}

// ─── useEnsureDiscipline ──────────────────────────────────────────────────────
let disciplinePromise: Promise<void> | null = null;
let disciplineLoaded = false;

function loadDisciplineData(dispatch: Dispatch<AppAction>) {
  disciplinePromise = api.gmDiscipline.records.list()
    .then(records => {
      dispatch({
        type: 'SET_DISCIPLINE_DATA',
        data: { records, loaded: true, loadError: undefined },
      });
      disciplineLoaded = true;
    })
    .catch(err => {
      console.error('[useEnsureDiscipline] failed:', err);
      disciplinePromise = null;
      // Mark loaded with an error so DisciplinePage can show a retry affordance
      // instead of an eternal loading state (the effect won't re-run while the
      // page stays mounted).
      dispatch({
        type: 'SET_DISCIPLINE_DATA',
        data: { loaded: true, loadError: err instanceof Error ? err.message : 'Failed to load disciplinary records' },
      });
    });
}

/**
 * Lazily fetch all disciplinary records on first mount of DisciplinePage.
 * One boot fetch per session; mutations keep the slice in sync via
 * DISCIPLINE_UPSERT/REMOVE, so no refetch is needed.
 */
export function useEnsureDiscipline() {
  const { dispatch } = useApp();
  useEffect(() => {
    if (disciplineLoaded || disciplinePromise) return;
    loadDisciplineData(dispatch);
  }, [dispatch]);
}

/** Re-run the discipline boot fetch after a failed load (see loadError). */
export function retryDiscipline(dispatch: Dispatch<AppAction>) {
  if (disciplinePromise) return;
  dispatch({ type: 'SET_DISCIPLINE_DATA', data: { loaded: false, loadError: undefined } });
  loadDisciplineData(dispatch);
}

// ─── useEnsureExecReports ─────────────────────────────────────────────────────
let execReportsPromise: Promise<void> | null = null;
let execReportsLoaded = false;

function loadExecReportsData(dispatch: Dispatch<AppAction>) {
  execReportsPromise = api.gmExecReports.reports.list()
    .then(reports => {
      dispatch({
        type: 'SET_EXEC_REPORTS_DATA',
        data: { reports, loaded: true, loadError: undefined },
      });
      execReportsLoaded = true;
    })
    .catch(err => {
      console.error('[useEnsureExecReports] failed:', err);
      execReportsPromise = null;
      // Mark loaded with an error so ExecReportsPage can show a retry
      // affordance instead of an eternal loading state (the effect won't
      // re-run while the page stays mounted).
      dispatch({
        type: 'SET_EXEC_REPORTS_DATA',
        data: { loaded: true, loadError: err instanceof Error ? err.message : 'Failed to load executive reports' },
      });
    });
}

/**
 * Lazily fetch all saved executive reports on first mount of ExecReportsPage.
 * One boot fetch per session; mutations keep the slice in sync via
 * EXEC_REPORTS_UPSERT/REMOVE, so no refetch is needed.
 */
export function useEnsureExecReports() {
  const { dispatch } = useApp();
  useEffect(() => {
    if (execReportsLoaded || execReportsPromise) return;
    loadExecReportsData(dispatch);
  }, [dispatch]);
}

/** Re-run the exec-reports boot fetch after a failed load (see loadError). */
export function retryExecReports(dispatch: Dispatch<AppAction>) {
  if (execReportsPromise) return;
  dispatch({ type: 'SET_EXEC_REPORTS_DATA', data: { loaded: false, loadError: undefined } });
  loadExecReportsData(dispatch);
}

// ─── useEnsureAdminUsers ──────────────────────────────────────────────────────
let adminUsersPromise: Promise<void> | null = null;
let adminUsersLoaded = false;

/**
 * Lazily fetch the full /api/users list (admin-only) and overlay the richer
 * role/tier/shift fields onto the directory baseline already in state.
 * Called by AdminPage on mount; non-admins get a graceful 401 that is caught.
 */
export function useEnsureAdminUsers() {
  const { state, dispatch } = useApp();
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (adminUsersLoaded) return;
    if (!adminUsersPromise) {
      adminUsersPromise = api.users.list()
        .then(profiles => {
          const baseline = stateRef.current.registeredUsers;
          const byId = new Map((profiles as import('../services/api').UserProfile[]).map(p => [p.id, p]));
          const merged: User[] = baseline.map(u => {
            const p = byId.get(u.id);
            if (!p) return u;
            return {
              ...u,
              name: p.displayName || u.name,
              shift: p.shift,
              tier: p.tier,
              role: p.role,
              avatar: p.avatarUrl || u.avatar,
              email: p.email || u.email,
            };
          });
          for (const p of profiles as import('../services/api').UserProfile[]) {
            if (!merged.some(u => u.id === p.id)) {
              merged.push({
                id: p.id,
                name: p.displayName,
                shift: p.shift,
                tier: p.tier,
                avatar: p.avatarUrl || '👤',
                role: p.role,
                email: p.email,
                entraOid: p.id,
              });
            }
          }
          dispatch({ type: 'SET_REGISTERED_USERS', users: merged });
          USERS.length = 0;
          USERS.push(...merged);
          adminUsersLoaded = true;
        })
        .catch(err => {
          // Non-admins get 401 here — expected, not an error.
          if (err?.status !== 401 && err?.message !== 'Unauthorized') {
            console.error('[useEnsureAdminUsers] failed:', err);
          }
          adminUsersPromise = null;
        });
    }
  }, [dispatch]);
}
